// Lightweight, browser-kompatibler Exchange Importer

import { parseExchangeInstant, parseBinanceInstant } from './exchangeTime.js';
import {
  normalizeExchangeAccountId as normalizeExchangeAccountIdShared,
  buildExchangeImportUid,
  buildExchangeRowFingerprint,
  buildExchangeAccountScopeKey,
  pickExchangeAccountId,
} from '../../shared/exchangeEventId.js';

// ---------- VeChain Whitelist ----------
const VECHAIN_TOKENS = [
  "VET","VTHO","USDGLO","HAI","B3TR","VOT3","SHA","VNS","BVET","YEET",
  "VEB3TR","WOV","COJ","VVET","SHT","VEED","MVA","VEUSD","WVET","VSEA","OCE","VPU","GOLD",
  "JUR","MVG","VFA","FCWS","VEX","LGCT","DHN","PLA","PPR","UNION",
  "DRAGON","BANANA","VST","DBET","EHRT","SNK","TIC","GEMS","AQD","MDN",
  "BAG","LION","SASS","BANGZ","BVC","PLG","SQUAD","MILK","RAIN","VSC",
  "UNO","DWVET","MVC","3DT","USDV","LLAMA","RATV"
];

const FIAT = new Set(["EUR","USD"]);
/** Quotes allowed temporarily for trade_raw pairing (incl. stables). */
const QUOTE_ASSETS = new Set(["EUR", "USD", "USDT", "USDC", "BTC", "ETH", "KRW"]);

const ALIASES = {
  "vtho":"VTHO", "vet":"VET", "b3tr":"B3TR",
  "wvet":"WVET", "veb3tr":"VEB3TR",
  "eur":"EUR", "usd":"USD" // Fiat-Währungen für VeChain-Trades
};

const CANONICAL = {
  // "WVET":"VET"
};

function normalizeSymbol(sym) {
  const raw = (sym ?? "").trim();
  if (!raw) return "";
  const up = (ALIASES[raw.toLowerCase()] ?? raw).toUpperCase();
  return CANONICAL[up] ?? up;
}

function isWhitelisted(sym) {
  const s = normalizeSymbol(sym);
  if (!s) return false;
  return VECHAIN_TOKENS.includes(s);
}

function isRelevantPair(base, quote) {
  // Für Trades: Base muss VeChain-Token sein, Quote kann Fiat sein
  return isWhitelisted(base);
}

// Export Whitelist-Funktionen für UI-Komponenten
export { normalizeSymbol, isWhitelisted, isRelevantPair, toEpochUtc, validateTimestamp, computeMedianShift, linkExchangeCustodyToChain, loadExchangeImports };

function normalizeRow(raw, detectedExchange, ctx) {
  const parser = getParser(detectedExchange?.id || 'unknown');

  const tx = parser(raw, ctx);
  if (!tx) return null;

  if (Array.isArray(tx)) {
    return tx.map(t => normalizeTransaction(t, ctx)).filter(t => t !== null);
  }

  return normalizeTransaction(tx, ctx);
}

function normalizeTransaction(tx, ctx = {}) {
  if (!tx) return null;

  // UTC-Normalisierung der Timestamps
  const rawTs = toEpochUtc(tx.timestamp);
  
  // 🚀 FIX: Validate timestamp with hard guardrails
  const ts = validateTimestamp(rawTs, tx.exchangeCsvTimestamp, tx.source || 'exchange');

  // Symbole sauber normalisieren (einheitlich in Großschrift)
  const assetFallback = normalizeSymbol(
    tx.asset ?? tx.currency ?? tx.base ?? tx.baseAsset
  );
  const accountId = pickExchangeAccountId(tx);
  const normalizedTx = {
    ...tx,
    timestamp: ts,
    exchangeAccountId: accountId,
    exchange_account_id: accountId,
    tz_adjusted: !!tx.exchangeTimeInterpretation?.startsWith('iana:'),
    exchangeTimeInterpretation: tx.exchangeTimeInterpretation || undefined,
    // 👇 Wichtig: auch baseAsset/quoteAsset berücksichtigen
    base:  normalizeSymbol(tx.base  ?? tx.baseAsset ?? tx.asset ?? tx.currency),
    quote: normalizeSymbol(tx.quote ?? tx.quoteAsset),
    feeAsset: normalizeSymbol(tx.feeAsset),
    // Asset-Fallback auch aus currency (OKX/Huobi trade_raw)
    asset: assetFallback,
  };

  // Offizielles V3Tax-Standard-CSV: exakt wie eingetragen, ohne VeChain-Whitelist
  if (tx.importSource === 'v3tax_standard') {
    if (!normalizedTx.timestamp) {
            return null;
    }
    return ensureImportUid(normalizedTx);
  }

  // 🔒 Whitelist-Filter:
  // - Trades: behalten, wenn Base ODER Quote whitelisted ist
  // - Deposits/Withdrawals: behalten, wenn Asset whitelisted ist
  // - Fees: wenn NUR FeeAsset whitelisted ist, behalten wir die Zeile ebenfalls
  // - trade_raw / ledger_trade_leg / transfer / fee: Zwischenformate bis postProcess
  const t = String(normalizedTx.type || '').toLowerCase();

  let keep = false;
  if (t === 'trade' || t === 'spot' || t === 'order' || t === 'fill') {
    keep = isRelevantPair(normalizedTx.base, normalizedTx.quote);
  } else if (t === 'trade_raw') {
    const a = normalizedTx.asset || normalizedTx.base;
    keep = isWhitelisted(a) || FIAT.has(a) || QUOTE_ASSETS.has(a);
  } else if (t === 'fee') {
    const feeSym =
      normalizedTx.feeAsset || normalizedTx.asset || normalizedTx.base;
    keep = isWhitelisted(feeSym) || FIAT.has(feeSym) || QUOTE_ASSETS.has(feeSym);
  } else if (
    t === 'deposit' ||
    t === 'withdrawal' ||
    t === 'transfer' ||
    t === 'staking' ||
    t === 'airdrop' ||
    t === 'income' ||
    t === 'ledger_trade_leg' ||
    t === 'account_transfer'
  ) {
    // Nur VeChain-Token, keine Fiat-only Transaktionen.
    // ⚠️ Ausnahme: Kraken Ledger Trades bestehen aus Legs pro Asset (inkl. EUR/USD).
    //             Damit wir Quote/Price aus refid rekonstruieren können, müssen FIAT-Legs TEMPORÄR durch.
    if (t === 'ledger_trade_leg') {
      keep =
        isWhitelisted(normalizedTx.asset) ||
        FIAT.has(normalizedTx.asset) ||
        QUOTE_ASSETS.has(normalizedTx.asset);
    } else if (t === 'transfer') {
      keep =
        isWhitelisted(normalizedTx.asset || normalizedTx.base) ||
        FIAT.has(normalizedTx.asset || normalizedTx.base);
    } else {
      keep =
        isWhitelisted(normalizedTx.asset || normalizedTx.base) &&
        !FIAT.has(normalizedTx.asset || normalizedTx.base);
    }
  }

  // Gebühren-only Relevanz (z.B. Fee in VTHO bei sonst „irrelevantem" Paar)
  if (!keep && isWhitelisted(normalizedTx.feeAsset)) {
    keep = true;
  }

  // nichts von uns? → verwerfen
  if (!keep) {
        return null;
  }

  // 🚀 FIX: Exclude transactions with invalid timestamps from processing
  if (!normalizedTx.timestamp) {
        return null;
  }

  return ensureImportUid(normalizedTx);
}

function ensureImportUid(tx) {
  if (tx?.importUid || tx?.import_uid) {
    return {
      ...tx,
      importUid: tx.importUid || tx.import_uid,
      import_uid: tx.import_uid || tx.importUid,
    };
  }
  const uid = buildExchangeImportUid(tx);
  return { ...tx, importUid: uid, import_uid: uid };
}

// ---------- Public API ----------
export async function importCsvFiles(fileList, options = {}) {
  const transactions = [];
  const validationErrors = [];
  const detectedExchanges = new Set();

  for (const file of fileList) {
    const fileTransactions = [];
    const fileIssues = [];

    try {
      const { rows, headers } = await readAnyTable(file, options);

      const detected = detectExchange(headers);
      const exchangeId = detected?.id || 'unknown';
      detectedExchanges.add(exchangeId.toUpperCase());

      const standardAttempt = looksLikeV3TaxStandardAttempt(headers);
      const isStandardFile = exchangeId === 'v3tax_standard';

      if (!detected && standardAttempt.isLikely) {
        fileIssues.push(
          makeImportIssue({
            file: file.name,
            column: 'headers',
            code: 'STANDARD_TEMPLATE_MISSING_HEADERS',
            message:
              `V3Tax standard template headers are incomplete. Missing required columns: ${standardAttempt.missingRequired.join(', ')}`,
            template: true,
            missingHeaders: standardAttempt.missingRequired,
          })
        );
        validationErrors.push(...fileIssues);
        continue;
      }

      const ctx = { file: file.name };

      if (!rows.length) {
        fileIssues.push(
          makeImportIssue({
            file: file.name,
            code: 'NO_DATA_ROWS',
            message: 'No data rows found in this file.',
            template: isStandardFile,
          })
        );
        validationErrors.push(...fileIssues);
        continue;
      }

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        try {
          const normalized = normalizeRow(row, detected, { ...ctx, rowIndex: i + 2 });

          if (!normalized) continue;

          if (Array.isArray(normalized)) {
            fileTransactions.push(...normalized);
          } else {
            fileTransactions.push(normalized);
          }
        } catch (e) {
          if (e?.importIssue) {
            fileIssues.push({
              ...e.importIssue,
              file: e.importIssue.file || file.name,
            });
          } else {
            fileIssues.push(
              makeImportIssue({
                file: file.name,
                row: i + 2,
                code: 'ROW_PARSE_ERROR',
                message: e?.message || String(e),
                template: isStandardFile,
              })
            );
          }
        }
      }

      if (isStandardFile && fileIssues.some((x) => (x?.severity || 'error') === 'error')) {
        validationErrors.push(...fileIssues);
        validationErrors.push(
          makeImportIssue({
            file: file.name,
            code: 'STANDARD_TEMPLATE_FILE_REJECTED',
            message:
              'This V3Tax standard file was not imported because it contains validation errors. Please fix the rows and upload it again.',
            template: true,
          })
        );
        continue;
      }

      transactions.push(...fileTransactions);
      validationErrors.push(...fileIssues);
    } catch (e) {
      if (e?.importIssue) {
        validationErrors.push({
          ...e.importIssue,
          file: e.importIssue.file || file.name,
        });
      } else {
        validationErrors.push(
          makeImportIssue({
            file: file.name,
            code: 'FILE_READ_ERROR',
            message: e?.message || String(e),
          })
        );
      }
    }
  }

  const rawRows = transactions.length;
  const { transactions: cleaned, issues: postIssues } = postProcess(transactions);
  const allIssues = [...validationErrors, ...(postIssues || [])];
  const blockingIssueCount = allIssues.filter(
    (x) => (x?.severity || 'error') === 'error'
  ).length;
  const hasBlockingErrors = blockingIssueCount > 0;

  return {
    transactions: cleaned,
    summary: {
      detectedExchanges: Array.from(detectedExchanges),
      total: cleaned.length,
      rawRows,
      relevantRows: rawRows,
      canonicalRows: cleaned.length,
      ignoredIrrelevantRows: 0,
      blockingIssueCount,
    },
    hasBlockingErrors,
    issues: allIssues,
    errors: allIssues.map(formatImportIssue),
    validationErrors: allIssues,
  };
}

// ---------- Reader (CSV/XLSX) ----------
function looksLikeEncryptedOfficeFile(buf) {
  const sig = new Uint8Array(buf.slice(0, 8));
  const cfb = [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1];
  return cfb.every((b, i) => sig[i] === b);
}

async function readXlsxViaBackend(file, password = '') {
  const form = new FormData();
  form.append('file', file);
  form.append('password', password || '');

  const res = await fetch('/api/exchange-imports/read-xlsx', {
    method: 'POST',
    body: form
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || !json?.success) {
    throw new Error(json?.message || 'XLSX could not be read');
  }

  return {
    headers: Array.isArray(json.headers) ? json.headers : [],
    rows: Array.isArray(json.rows) ? json.rows : []
  };
}

async function readAnyTable(file, options = {}) {
  const buf = await file.arrayBuffer();
  const isXlsx = /\.xlsx$/i.test(file.name);

  if (isXlsx) {
    const isEncryptedOffice = looksLikeEncryptedOfficeFile(buf);
    let password = '';
    if (isEncryptedOffice) {
      password =
        typeof options?.passwordResolver === 'function'
          ? await options.passwordResolver(file)
          : (options?.password || '');

      if (!password) {
        throw new Error(`Password required for encrypted XLSX: ${file.name}`);
      }
    }

    // Auch normale XLSX konsistent über Backend lesen.
    return await readXlsxViaBackend(file, password);
  }

  // CSV fallback (einfach, robust)
  const text = new TextDecoder('utf-8').decode(buf).replace(/\uFEFF/g, '');
  const { headers, rows } = parseCsv(text);
  return { headers, rows };
}

function parseCsv(text) {
  const rawLines = text.split(/\r?\n/).filter(l => l.trim().length);
  if (!rawLines.length) return { headers: [], rows: [] };

  // 🚀 NEU: Delimiter Auto-Detection
  const delimiter = detectDelimiter(rawLines);
  
  // Kandidaten-Header in den ersten 50 Zeilen suchen
  const limit = Math.min(rawLines.length, 50);
  let headerLineIndex = 0;
  let bestCols = [];
  for (let i = 0; i < limit; i++) {
    const cols = splitCsvLine(rawLines[i], delimiter);
    // Heuristik: "echte" Header haben mehrere Spalten
    const looksTabular = cols.length >= 4;
    const hasKeywords = cols.some(c =>
      /timestamp|transaction id|transaction type|in\/out|amount fiat/i.test(c)
    );
    if (looksTabular && hasKeywords) { headerLineIndex = i; bestCols = cols; break; }
    if (cols.length > bestCols.length) { bestCols = cols; headerLineIndex = i; }
  }

  const headersRaw = bestCols.map(h => String(h || '').trim());
  const headers = headersRaw.map(h => h.replace(/^"+|"+$/g, '')); // strip surrounding quotes
  const rows = [];
  for (let i = headerLineIndex + 1; i < rawLines.length; i++) {
    const cols = splitCsvLine(rawLines[i], delimiter);
    if (cols.length === 1 && !cols[0]) continue; // leere Zeilen auslassen
    rows.push(objectFromRow(headers, cols));
  }
  return { headers, rows };
}

// 🚀 NEU: Globaler Delimiter für EU-Zahlenformat
let detectedSep = ',';

// 🚀 NEU: Delimiter Auto-Detection
function detectDelimiter(lines) {
  const sampleLines = lines.slice(0, Math.min(5, lines.length));
  const commaCounts = [];
  const semicolonCounts = [];
  
  for (const line of sampleLines) {
    // Zähle Kommas und Semikolons außerhalb von Anführungszeichen
    let commaCount = 0;
    let semicolonCount = 0;
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        // Escaped quotes
        if (inQuotes && line[i + 1] === '"') {
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (!inQuotes) {
        if (char === ',') commaCount++;
        if (char === ';') semicolonCount++;
      }
    }
    
    commaCounts.push(commaCount);
    semicolonCounts.push(semicolonCount);
  }
  
  const avgCommas = commaCounts.reduce((a, b) => a + b, 0) / commaCounts.length;
  const avgSemicolons = semicolonCounts.reduce((a, b) => a + b, 0) / semicolonCounts.length;
  
    
  // Wähle den häufigeren Delimiter und speichere global
  detectedSep = avgSemicolons > avgCommas ? ';' : ',';
  return detectedSep;
}

function splitCsvLine(line, delimiter = ',') {
  const out = [];
  let cur = '', q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"' ) {
      if (q && line[i+1] === '"') { cur += '"'; i++; } else { q = !q; }
    } else if (c === delimiter && !q) { out.push(cur); cur = ''; }
    else { cur += c; }
  }
  out.push(cur);
  return out.map(s => s.trim());
}

function objectFromRow(headers, cols) {
  const obj = {};
  for (let i = 0; i < headers.length; i++) {
    const key = headers[i];
    let v = (cols[i] ?? '').trim();
    // strip quotes around cell, aber nur wenn vollständig umschlossen
    if (v.startsWith('"') && v.endsWith('"')) v = v.slice(1, -1);
    obj[key] = v;
  }
  return obj;
}

// ---------- Detection (DE/EN + fuzzy) ----------
function detectExchange(rawHeaders) {
  const headers = rawHeaders.map(norm);
    
  const candidates = Object.entries(aliasSets).map(([id, def]) => {
    // 🚀 NEU: Robuste OR-Logik für alternative Schemata
    // Unterstützt sowohl alte Struktur (Array von Arrays) als auch neue Struktur (Array von Schemata)
    let schemaHits = [];
    
    if (Array.isArray(def.required) && def.required.length > 0) {
      // Prüfe ob es die neue Struktur ist (Array von Schemata)
      if (Array.isArray(def.required[0]) && Array.isArray(def.required[0][0])) {
        // Neue Struktur: Array von Schemata (z.B. Bitpanda)
        schemaHits = def.required.map(schema => 
          schema.every(group => 
            group.some(alias => headers.some(h => containsFuzzy(h, alias)))
          )
        );
      } else {
        // Alte Struktur: Array von Gruppen (z.B. Binance)
        schemaHits = [def.required.every(group => 
          group.some(alias => headers.some(h => containsFuzzy(h, alias)))
        )];
      }
    }
    
    const requiredHit = schemaHits.some(hit => hit); // Mindestens ein Schema erfüllt
    const bonus = (def.hints || []).reduce((acc, hint) =>
      acc + (headers.some(h => containsFuzzy(h, hint)) ? 1 : 0), 0);
    
    // Spezifische Spalten geben höhere Priorität
    let specificityBonus = 0;
    if (id === 'bitpanda') {
      if (headers.includes('currency out')) specificityBonus += 10;
      if (headers.includes('currency in')) specificityBonus += 10;
      if (headers.includes('amount out')) specificityBonus += 10;
      if (headers.includes('amount in')) specificityBonus += 10;
      if (headers.includes('transaction type')) specificityBonus += 5;
      if (headers.includes('asset class')) specificityBonus += 5;
      if (headers.includes('transaction id')) specificityBonus += 8;
      if (headers.includes('asset market price')) specificityBonus += 8;
      if (headers.includes('bitpanda')) specificityBonus += 15;
    } else if (id === 'mexc') {
      if (headers.includes('uid')) specificityBonus += 20;
      if (headers.includes('pairs')) specificityBonus += 10;
      if (headers.includes('role')) specificityBonus += 12;
      if (headers.includes('executed amount')) specificityBonus += 8;
      if (headers.includes('average filled price')) specificityBonus += 8;
      if (headers.includes('order amount')) specificityBonus += 6;
      if (headers.includes('withdrawal address')) specificityBonus += 6;
      if (headers.includes('progress')) specificityBonus += 4;
    } else if (id === 'v3tax_standard') {
      const hasKind = headers.some((h) => containsFuzzy(h, 'kind') || containsFuzzy(h, 'art'));
      const hasSource = headers.some((h) => containsFuzzy(h, 'source') || containsFuzzy(h, 'quelle'));
      if (hasKind && hasSource) specificityBonus += 35;
      if (
        headers.some((h) => containsFuzzy(h, 'timestamp') || containsFuzzy(h, 'zeitstempel'))
      ) {
        specificityBonus += 20;
      }
    } else if (id === 'binance') {
      if (headers.includes('time') && headers.includes('coin') && headers.includes('network') && headers.includes('txid')) {
        specificityBonus += 18;
      }
      if (headers.includes('spend amount') && headers.includes('receive amount')) {
        specificityBonus += 16;
      }
      if (headers.includes('fee coin')) specificityBonus += 8;
      if (headers.includes('user id') && headers.includes('account') && headers.includes('operation') && headers.includes('change')) {
        specificityBonus += 10;
      }
    } else if (id === 'bitget') {
      // nur echte Bitget-spezifische Sachen bonifizieren
      if (headers.includes('instrument')) specificityBonus += 12;
      if (headers.includes('order id')) specificityBonus += 8;
      if (headers.includes('fee asset')) specificityBonus += 6;
      if (headers.includes('detail') || headers.includes('remark')) specificityBonus += 6;
      if (headers.includes('realized pnl') || headers.includes('pnl')) specificityBonus += 8;
      if (headers.includes('bitget')) specificityBonus += 15;

      // MEXC-typische Header als Gegensignal
      if (headers.includes('uid')) specificityBonus -= 20;
      if (headers.includes('role')) specificityBonus -= 10;
      if (headers.includes('pairs') && !headers.includes('pair')) specificityBonus -= 10;
    }
    
    const totalScore = (requiredHit ? 100 : 0) + bonus + specificityBonus;
        
    return { id, score: totalScore, requiredHit };
  });
  
  // 🚀 NEU: Nur Kandidaten mit erfülltem Schema berücksichtigen
  const validCandidates = candidates.filter(c => c.requiredHit);
  const result = validCandidates.length > 0 
    ? validCandidates.sort((a,b)=>b.score-a.score)[0]
    : null;
    
      return result;
}

const norm = s => s.toLowerCase().replace(/\s+/g,' ').replace(/[^a-z0-9 _\/\-\(\)\[\]:]/g,'');
const containsFuzzy = (h, kw) => {
  if (!h || !kw) return false;
  return h.includes(kw) || levenshtein(h, kw) <= Math.max(1, Math.floor(kw.length/6));
};

function levenshtein(a,b){
  // Defensive programming: handle null/undefined inputs
  if (!a || !b) return a === b ? 0 : Math.max((a || '').length, (b || '').length);
  if (typeof a !== 'string' || typeof b !== 'string') return Math.max(String(a || '').length, String(b || '').length);
  
  const m=[];let i,j;for(i=0;i<=b.length;i++){m[i]=[i]}for(j=0;j<=a.length;j++){m[0][j]=j}for(i=1;i<=b.length;i++){for(j=1;j<=a.length;j++){m[i][j]=b.charAt(i-1)===a.charAt(j-1)?m[i-1][j-1]:Math.min(m[i-1][j-1]+1,m[i][j-1]+1,m[i-1][j]+1)}}return m[b.length][a.length]
}

// Minimaler Alias-Satz (erweitern wir sukzessive)
const aliasSets = {
  v3tax_standard: {
    required: [
      [
        ['source', 'quelle', 'quelle (optional)'],
        ['kind', 'art', 'typ', 'vorgang'],
        ['timestamp_utc', 'timestamp utc', 'zeitstempel utc', 'zeitstempel'],
        ['base', 'token', 'asset'],
        ['amount', 'menge']
      ]
    ],
    hints: [
      'side', 'buy / sell', 'kauf / verkauf', 'richtung',
      'quote', 'gegenwert',
      'quote_amount', 'quote amount', 'gegenwert-betrag',
      'price', 'preis',
      'fee', 'gebühr', 'gebuehr',
      'fee_asset', 'fee asset', 'gebühren-asset', 'gebuehren-asset',
      'txid',
      'notes', 'notiz'
    ]
  },
  binance: {
    // ✅ Binance.com: mehrere offizielle Exporte (OR-Logik)
    required: [
      // 1) Spot Trade History (CSV/XLSX)
      [
        ['date(utc)','date','time','timestamp','date(utc+0)','date(utc+1)'],
        ['pair','market','symbol'],
        ['type','side','buy','sell','seite','typ','richtung'],
        ['price','preis','kurs'],
        ['amount','executed','quantity','vol','menge','volumen','anzahl'],
        ['fee','gebühr','commission']
      ],

      // 2) Spot Trade History XLSX
      [
        ['date(utc)','date(utc+0)','date(utc+1)','time','timestamp'],
        ['pair'],
        ['type'],
        ['price'],
        ['amount'],
        ['total'],
        ['fee'],
        ['fee coin','fee asset']
      ],

      // 3) Deposit History CSV/XLSX (u.a. Header "Time")
      [
        ['date(utc+0)','date(utc)','date','time','timestamp'],
        ['coin','asset'],
        ['network'],
        ['amount'],
        ['address'],
        ['txid','txhash','transaction id'],
        ['status']
      ],

      // 4) Withdraw History CSV/XLSX
      [
        ['date(utc+0)','date(utc)','date','time','timestamp'],
        ['coin','asset'],
        ['network'],
        ['amount'],
        ['fee','transaction fee'],
        ['address'],
        ['txid','txhash','transaction id'],
        ['status']
      ],

      // 5) Buy Crypto / Fiat Order History XLSX (u.a. Header "Time")
      [
        ['date(utc+1)','date(utc)','date','time','timestamp'],
        ['method','payment'],
        ['spend amount','spend'],
        ['receive amount','receive'],
        ['fee'],
        ['price'],
        ['status'],
        ['transaction id','order id']
      ],

      // 6) Binance Transaction History CSV (von MEXC-Funding abgrenzen)
      [
        ['user id'],
        ['time'],
        ['account'],
        ['operation'],
        ['coin','asset'],
        ['change','amount'],
        ['remark','notes']
      ]
    ],
    hints: [
      'fee coin','total','network','address',
      'receive amount','spend amount',
      'transaction id','txid',
      'account','operation','change','remark'
    ]
  },
  binance_us: {
    // ✅ Binance.US hat ein eigenes CSV-Schema (nicht kompatibel zu Binance.com)
    required: [
      [
        ['user id'],
        ['time'],
        ['category'],
        ['operation'],
        ['order id'],
        ['transaction id'],
        ['primary asset'],
        ['realized amount for primary asset'],
        ['base asset'],
        ['realized amount for base asset'],
        ['quote asset'],
        ['realized amount for quote asset'],
        ['fee asset'],
        ['realized amount for fee asset']
      ]
    ],
    hints: ['payment method','withdraw method','additional note','realized amount for quote asset in usd','realized amount for fee asset in usd']
  },
  kraken: {
    required: [
      // Trades-Export
      [['txid'],['ordertxid'],['pair'],['time'],['type'],['price'],['cost'],['fee'],['vol']],
      // Ledgers-Export  
      [['txid'],['time'],['type'],['aclass'],['asset'],['amount'],['fee'],['balance']]
    ],
    hints: ['ordertype','ledgers','margin','misc','subtype']
  },
  coinbase: {
    // ✅ Coinbase hat mehrere Varianten ("Transactions" Export + Advanced Trade/Fills)
    // -> mehrere Schemata (OR-Logik)
    required: [
      // 1) Coinbase "Transactions" Export (Legacy/Current)
      [
        ['timestamp'],
        ['transaction type'],
        ['asset'],
        ['quantity transacted'],
        ['spot price currency', 'price currency'],
        ['spot price at transaction', 'price at transaction'],
      ],
      // 2) Coinbase Advanced Trade (Fills / Trades) - grob
      [
        ['trade id','id'],
        ['created at','timestamp','time'],
        ['side'],
        ['product','pair','market'],
        ['price'],
        ['size','filled','amount','qty'],
      ]
    ],
    hints: [
      'total (inclusive of fees',
      'fees and/or spread',
      'notes',
      'subtotal',
      'price currency',
      'price at transaction',
      'advanced trade',
      'trade id'
    ]
  },
  bitpanda: {
    required: [
      // Neue Bitpanda-Struktur - spezifischere Spalten
      [['date', 'timestamp', 'datum'], ['type', 'transaction type'], ['amount out', 'spend'], ['currency out', 'currency'], ['amount in', 'receive'], ['currency in']],
      // Legacy-Format - spezifischere Spalten
      [['transaction id'], ['timestamp'], ['transaction type'], ['in/out'], ['amount fiat'], ['fiat'], ['amount asset'], ['asset'], ['asset market price'], ['asset class'], ['fee'], ['fee asset']]
    ],
    hints: ['payment method', 'wallet', 'transaction reference', 'product id', 'account', 'value', 'value currency', 'swap', 'convert', 'reward', 'staking', 'best vip', 'bitpanda', 'currency out', 'currency in', 'amount out', 'amount in']
  },
  bybit: {
    required: [
      // Trade History Export
      [['trade time', 'filled time', 'date(utc)'], ['symbol', 'spot pair', 'pair'], ['side', 'direction'], 
       ['filled price'], ['filled qty', 'filled quantity'], ['filled value']],
      // Asset Change Details Export
      [['date'], ['type'], ['coin', 'asset'], ['amount', 'change'], ['description']]
    ],
    hints: ['fees', 'order no', 'transaction id', 'order type', 'fee paid', 'fee asset', 'tradingfee', 'deposit', 'withdraw', 'rebate', 'commission', 'interest']
  },
  gateio: {
    required: [
      // Spot Trade History Export
      [['date', 'time'], ['market', 'pair'], ['type', 'trade type', 'side'], ['price'], ['amount'], ['total']],
      // Deposit Export
      [['date', 'time'], ['coin'], ['amount'], ['txid', 'transaction id']],
      // Withdrawal Export
      [['date', 'time'], ['coin'], ['amount'], ['txid', 'transaction id'], ['fee']]
    ],
    hints: ['fee', 'order id', 'fee coin', 'status', 'network', 'margin type', 'funding cost', 'liquidation']
  },
  mexc: {
    required: [
      // 1) echte Spot Trade History (dein Upload)
      [
        ['uid'],
        ['pairs'],
        ['time'],
        ['side'],
        ['filled price'],
        ['executed amount'],
        ['total'],
        ['fee'],
        ['role']
      ],

      // 2) echte Spot Order History (dein Upload)
      [
        ['uid'],
        ['pairs'],
        ['time'],
        ['type'],
        ['direction'],
        ['average filled price'],
        ['filled quantity'],
        ['order amount'],
        ['status']
      ],

      // 3) Funding History / Deposit / Withdrawal (streng: generische "amount" vermeidet Binance D/W)
      [
        ['crypto', 'coin', 'currency', 'asset'],
        ['time', 'date', 'timestamp'],
        ['txid', 'transaction id', 'transaction hash', 'hash', 'withdraw id', 'deposit id'],
        ['deposit amount', 'request amount', 'settlement amount', 'withdrawal amount', 'actual amount', 'net amount']
      ],

      // 4) Spot Statement / Events / Savings / Other
      [
        ['transaction type', 'type', 'record type', 'category', 'operation'],
        ['crypto', 'coin', 'currency', 'asset'],
        ['direction', 'in/out', 'flow'],
        ['amount', 'quantity', 'change', 'net amount'],
        ['time', 'date', 'timestamp']
      ],

      // 5) Convert / Small Asset Conversion / In-Out Exporte
      [
        ['currency in', 'to currency', 'receive currency'],
        ['amount in', 'to amount', 'receive amount'],
        ['currency out', 'from currency', 'pay currency', 'currency'],
        ['amount out', 'from amount', 'pay amount', 'amount'],
        ['time', 'date', 'timestamp']
      ]
    ],
    hints: [
      'uid',
      'pairs',
      'filled price',
      'executed amount',
      'average filled price',
      'filled quantity',
      'order amount',
      'role',
      'record type',
      'direction',
      'txid',
      'deposit amount',
      'request amount',
      'settlement amount',
      'trading fee',
      'withdrawal address',
      'progress',
      'memo',
      'network',
      'address',
      'events',
      'savings',
      'small asset',
      'mx conversion',
      'withdraw',
      'deposit'
    ]
  },
  bitget: {
    required: [
      // Order History / Trade History Export
      [['date', 'time'], ['pair', 'instrument'], ['side'], ['price'], ['filled', 'amount'], ['total']],
      // Asset History (Fund Flow) Export
      [['date', 'time'], ['type'], ['asset'], ['amount'], ['detail', 'remark']],
      // Account Statement Export
      [['date', 'time'], ['type'], ['asset'], ['amount'], ['pnl', 'realized pnl']]
    ],
    hints: ['fee', 'fee asset', 'order id', 'trade id', 'margin', 'instrument', 'settlement', 'rebate', 'transfer']
  },
  okx: {
    required: [
      // Trading Account History (Ledger-ähnlich)
      [['date', 'time'], ['type'], ['currency'], ['amount'], ['balance']]
    ],
    hints: ['order id', 'txid', 'sub-type', 'account', 'fee', 'mining', 'staking', 'reward', 'transfer']
  },
  huobi: {
    required: [
      // Englische Header (Standard)
      [['date', 'time'], ['type'], ['asset'], ['amount'], ['price'], ['fee']],
      [['date', 'time'], ['type'], ['asset'], ['amount'], ['transfer id', 'txid']],
      // Deutsche Header (falls verfügbar)
      [['datum', 'zeit'], ['typ'], ['asset', 'währung'], ['menge', 'betrag'], ['preis'], ['gebühr']],
      [['datum', 'zeit'], ['typ'], ['asset', 'währung'], ['menge', 'betrag'], ['transfer id', 'txid']],
      // Chinesische Header (falls verfügbar)
      [['时间'], ['类型'], ['资产'], ['数量'], ['价格'], ['手续费']],
      [['时间'], ['类型'], ['资产'], ['数量'], ['转账id', '交易id']],
      // Japanische Header (falls verfügbar)
      [['日時'], ['種類'], ['資産'], ['数量'], ['価格'], ['手数料']],
      [['日時'], ['種類'], ['資産'], ['数量'], ['転送id', '取引id']]
    ],
    hints: ['notes', 'in/out', 'inout', 'amount in', 'amount out', 'received', 'paid', 'crypto bonus', 'fork airdrop', 'reward', 'interest', 'margin trade', 'futures trade', '备注', '入出', '收到', '支付', '奖励', '利息']
  },
  crypto_com: {
    required: [
      // Crypto.com App/Web: "Transaktionsverlauf" Export (NEU)
      [['timestamp (utc)'], ['transaction kind'], ['currency'], ['amount'], ['transaction hash']],
      // Crypto.com Exchange: Trade History CSV
      [['date', 'timestamp'], ['market', 'pair'], ['side'], ['price'], ['quantity', 'amount'], ['total']],
      // Crypto.com Exchange: Wallet Transactions History
      [['date', 'timestamp'], ['description', 'type'], ['asset', 'currency', 'market'], ['amount', 'betrag'], ['balance']],
      // Crypto.com Exchange: Deposits & Withdrawals CSV
      [['date', 'timestamp'], ['description', 'type'], ['asset', 'currency'], ['amount', 'betrag'], ['txid', 'transaction id', 'hash']],
      // Crypto.com App: Crypto Wallet CSV
      [['date', 'timestamp'], ['description', 'type'], ['asset', 'currency'], ['amount', 'betrag']],
      // Crypto.com App: Cash Wallet CSV  
      [['date', 'timestamp'], ['description', 'type'], ['currency', 'währung'], ['amount', 'betrag']],
      // Crypto.com App: Visa Card CSV
      [['date', 'timestamp'], ['description', 'type'], ['currency'], ['amount', 'betrag']]
    ],
    hints: ['transaction description', 'to currency', 'to amount', 'native currency', 'native amount', 'value', 'gegenwert', 'usd value', 'fee', 'fee currency', 'supercharger', 'staking', 'reward', 'earn', 'interest', 'bonus', 'referral', 'promotion', 'visa card top-up', 'card top-up', 'send', 'receive', 'top-up', 'sepa', 'withdrawal', 'cashback', 'rebate', 'instrument', 'remark', 'order id', 'network', 'status', 'lockup', 'trade spot', 'trade futures']
  },
  upbit: {
    required: [
      // Koreanische Header (primär)
      [['날짜/시간', 'date', 'time'], ['구분', 'type', 'side'], ['자산', 'asset', 'coin'], 
       ['주문량', 'amount', 'quantity'], ['체결가', 'price'], ['체결금액', 'total'], ['수수료', 'fee']],
      // Englische Header (falls verfügbar)
      [['date', 'time'], ['side'], ['asset'], ['amount'], ['price'], ['total'], ['fee']]
    ],
    hints: ['krw', 'btc', 'eth', 'usdt', '매수', '매도', 'buy', 'sell', 'upbit']
  },
  bitvavo: {
    // ✅ EIN Schema (AND), nicht mehrere Mini-Schemata (OR)
    // Bitvavo hat i.d.R. Fee-Spalten — das macht es eindeutig
    required: [
      ['date', 'datum'],
      ['time', 'uhrzeit'],
      ['type'],
      ['currency', 'asset', 'received / paid currency'],
      ['amount', 'received / paid amount'],
      // Bitvavo hat i.d.R. Fee-Spalten — das macht es eindeutig
      ['fee currency', 'fee asset'],
      ['fee amount', 'fee']
    ],
    hints: ['timezone', 'quote currency', 'quote price', 'transaction id', 'address', 'bitvavo']
  },
  // TODO: lbank, bitmart, xt
  unknown: { required: [[]], hints: [] }
};

// ---------- Parsers (erste 3 voll funktionsfähig) ----------
function getParser(id) {
  switch(id){
    case 'v3tax_standard': return parseV3TaxStandardRow;
    case 'binance': return parseBinance;
    case 'binance_us': return parseBinanceUS;
    case 'kraken': return parseKraken;
    case 'coinbase': return parseCoinbase;
    case 'bitpanda': return parseBitpanda;
    case 'bybit': return parseBybit;
    case 'gateio': return parseGateIo;
    case 'mexc': return parseMexcFixed;
    case 'bitget': return parseBitget;
    case 'okx': return parseOkx;
    case 'huobi': return parseHuobi;
    case 'crypto_com': return parseCryptoCom;
    case 'upbit': return parseUpbit;
    case 'bitvavo': return parseBitvavo;
    default: return parseGenericSpotRow;
  }
}

function binanceTs(row) {
  return parseBinanceInstant(row, normalizeBinanceDate).iso;
}

function binanceTsMeta(row) {
  return parseBinanceInstant(row, normalizeBinanceDate);
}

function parseBinance(row, ctx){
  const exchangeAccountId =
    row['User ID'] || row['Account'] || row['UID'] || undefined;
  const binanceTx = (params) => normalTx({ exchangeAccountId, ...params });

  // --- 1) Deposit / Withdraw History (XLSX) ---
  if ((row['Coin'] || row['coin']) && (row['TXID'] || row['Txid'] || row['txid'] || row['TxID'])) {
    const { iso: ts, interpretation: exchangeTimeInterpretation } = binanceTsMeta(row);

    const asset = String(row['Coin'] || row['coin'] || '').toUpperCase();
    const amount = num(row['Amount'] ?? row['amount']);
    const fee = num(row['Fee'] ?? row['fee']);
    const address = row['Address'] || row['address'];
    const network = row['Network'] || row['network'];
    const txId = row['TXID'] || row['Txid'] || row['txid'] || row['TxID'];
    const status = row['Status'] || row['status'];

    if (!ts || !asset || !isNum(amount)) return null;

    // Heuristik: Withdraw-Export hat "Fee"-Spalte, Deposit meist nicht
    const type = isNum(fee) ? 'withdrawal' : 'deposit';

    return binanceTx({
      exchange:'binance', ctx, ts, type,
      base: asset, baseAmount: Math.abs(amount),
      feeAmount: isNum(fee) ? Math.abs(fee) : undefined,
      feeAsset: isNum(fee) ? asset : undefined,
      txId,
      exchangeTimeInterpretation,
      note: `Binance ${type}${network ? ` (${network})` : ''}${status ? ` - ${status}` : ''}${address ? ` -> ${address}` : ''}`
    });
  }

  // --- 2) Buy Crypto / Fiat Order History (XLSX) ---
  if (row['Spend Amount'] && row['Receive Amount'] && (row['Transaction ID'] || row['Transaction Id'] || row['transaction id'])) {
    const { iso: ts, interpretation: exchangeTimeInterpretation } = binanceTsMeta(row);

    const spend = parseBinanceAmountWithAsset(row['Spend Amount']);     // z.B. "20.00 EUR"
    const receive = parseBinanceAmountWithAsset(row['Receive Amount']); // z.B. "2899.30 VET"
    const feeParsed = parseBinanceAmountWithAsset(row['Fee']);          // z.B. "0.40 EUR"

    const txId = row['Transaction ID'] || row['Transaction Id'] || row['transaction id'] || row['Order ID'] || row['order id'];
    const status = row['Status'] || row['status'];
    const method = row['Method'] || row['method'];

    if (!ts || !spend?.asset || !receive?.asset || !isNum(spend.amount) || !isNum(receive.amount)) return null;

    const base = receive.asset;
    const quote = spend.asset;
    const baseAmount = Math.abs(receive.amount);
    const quoteAmount = Math.abs(spend.amount);

    // ✅ Preis robust aus Spend/Receive (nicht aus "Price"-Text, der oft invertiert ist)
    const price = baseAmount ? (quoteAmount / baseAmount) : undefined;

    return binanceTx({
      exchange:'binance', ctx, ts, type:'trade', side:'buy',
      base, baseAmount,
      quote, quoteAmount,
      price,
      feeAmount: feeParsed?.amount != null ? Math.abs(feeParsed.amount) : undefined,
      feeAsset: feeParsed?.asset || quote,
      txId,
      exchangeTimeInterpretation,
      note: `Binance Buy Crypto${method ? ` (${method})` : ''}${status ? ` - ${status}` : ''}`
    });
  }

  // --- 2c) Spot Order History (Fallback; wird in postProcess verworfen wenn trade_history existiert) ---
  const isBinanceOrderHistory =
    row['OrderNo'] != null ||
    row['Order No'] != null ||
    row['Average Price'] != null ||
    row['Trading total³'] != null ||
    row['Trading Total'] != null;

  if (isBinanceOrderHistory) {
    const orderTs = binanceTs(row);

    const status = String(row['Status'] || '').toUpperCase();
    if (status && !status.includes('FILLED') && !status.includes('PARTIALLY')) return null;

    const orderPairRaw = (row['Pair'] || '').toUpperCase();
    const [orderBase, orderQuote] = splitPair(orderPairRaw.replace(/\//g, ''));

    const orderSide = normalizeSide(row['Side'] || row['Type¹'] || row['Type'] || '');
    const orderPrice = num(row['Average Price'] || row['Average Filled Price'] || row['Price'] || row['Order Price']);
    const orderBaseAmount = parseBinanceAmount(row['Executed²'] || row['Executed'] || row['Filled Quantity'] || row['Amount']);
    const orderQuoteAmount = parseBinanceAmount(row['Trading total³'] || row['Trading Total'] || row['Total']);

    if (!orderTs || !orderBase || !orderQuote || !orderSide || !isNum(orderPrice) || !isNum(orderBaseAmount)) {
      return null;
    }

    return {
      ...binanceTx({
        exchange: 'binance',
        ctx,
        ts: orderTs,
        type: 'trade',
        side: orderSide,
        base: orderBase,
        baseAmount: orderBaseAmount,
        quote: orderQuote,
        quoteAmount: isNum(orderQuoteAmount) ? orderQuoteAmount : round(orderPrice * orderBaseAmount),
        price: orderPrice,
        feeAmount: undefined,
        feeAsset: undefined,
        txId: row['OrderNo'] || row['Order No'] || undefined,
        note: `Binance order history ${status}`.trim()
      }),
      importSource: 'order_history'
    };
  }

  // --- 3) Spot Trade History (CSV/XLSX) ---
  const ts = binanceTs(row);

  const pair = (row['Pair'] || row['Market'] || row['Symbol'] || row['Paar'] || row['Handelspaar'] || '').toUpperCase();

  // --- 2b) Earn / Rewards / Staking / Distribution ---
  const genericTypeRaw = String(
    row['Type'] ||
    row['Operation'] ||
    row['Transaction Type'] ||
    row['Category'] ||
    row['Remark'] ||
    ''
  ).toLowerCase().trim();

  const genericAsset = String(
    row['Coin'] ||
    row['Asset'] ||
    row['Primary Asset'] ||
    ''
  ).toUpperCase();

  const genericAmount = num(
    row['Amount'] ??
    row['Change'] ??
    row['Quantity'] ??
    row['Realized Amount For Primary Asset'] ??
    row['Realized Amount for Primary Asset']
  );

  const genericTxId =
    row['Transaction ID'] ||
    row['Transaction Id'] ||
    row['transaction id'] ||
    row['Order ID'] ||
    row['order id'] ||
    row['TXID'] ||
    row['Txid'] ||
    row['txid'] ||
    '';

  const isBinanceIncome =
    genericTypeRaw.includes('reward') ||
    genericTypeRaw.includes('staking') ||
    genericTypeRaw.includes('earn') ||
    genericTypeRaw.includes('interest') ||
    genericTypeRaw.includes('airdrop') ||
    genericTypeRaw.includes('distribution');

  if (
    ts &&
    !pair &&
    genericAsset &&
    isNum(genericAmount) &&
    isBinanceIncome
  ) {
    return binanceTx({
      exchange:'binance', ctx, ts, type:'income',
      base: genericAsset, baseAmount: Math.abs(genericAmount),
      txId: genericTxId,
      note: `Binance ${genericTypeRaw || 'income'}`
    });
  }

  let base = (row['Base Asset'] || row['Base'] || '').toString().toUpperCase();
  let quote = (row['Quote Asset'] || row['Quote'] || '').toString().toUpperCase();
  if (!base || !quote) {
    const split = splitPair(pair);
    base = split[0];
    quote = split[1];
  }

  const side = normalizeSide(row['Side'] || row['Type'] || row['Seite'] || row['Typ'] || row['Richtung'] || '');
  const price = num(row['Price'] || row['Preis'] || row['Kurs']);

  const baseAmount = parseBinanceAmount(row['Executed'] || row['Amount'] || row['Quantity'] || row['Menge'] || row['Volumen'] || row['Anzahl']);
  const quoteAmount = parseBinanceAmount(row['Total'] || row['Quote Quantity'] || row['Gesamtbetrag']);

  const feeCoin = (row['Fee Coin'] || row['Fee Asset'] || '').toString().toUpperCase();
  const feeRaw = row['Fee'] || row['Gebühr'] || row['Spread'];
  const feeParsed = feeCoin ? { amount: num(feeRaw), asset: feeCoin } : parseBinanceFee(feeRaw);

  if (!ts || !base || !quote || !side || !isNum(price) || !isNum(baseAmount)) return null;

  return {
    ...binanceTx({
      exchange:'binance', ctx, ts, type:'trade', side,
      base, baseAmount,
      quote, quoteAmount: isNum(quoteAmount) ? quoteAmount : round(price * baseAmount),
      price,
      feeAmount: feeParsed.amount, feeAsset: feeParsed.asset || quote
    }),
    importSource: 'trade_history'
  };
}

// Binance-spezifische Amount-Parsing (entfernt Währungssuffix)
function parseBinanceAmount(value) {
  if (value == null || value === '') return null;
  const str = String(value).replace(/[",]/g, '').trim();
  const match = str.match(/^(-?[\d.]+)\s*([A-Za-z0-9]+)?$/);
  if (match) return num(match[1]);
  return num(str);
}

function parseBinanceAmountWithAsset(value) {
  if (value == null || value === '') return null;
  const str = String(value).replace(/[",]/g, '').trim();
  const match = str.match(/^(-?[\d.]+)\s*([A-Za-z0-9/]+)?$/);
  if (!match) return { amount: num(str), asset: null };
  const amount = num(match[1]);
  let asset = match[2] || null;
  if (asset && asset.includes('/')) asset = asset.split('/')[0] || null;
  return { amount, asset };
}

// Generic "amount + asset" parser (z.B. "0.0019USDT", "0.0019 USDT", "6000" -> asset undefined)
function parseAmountWithAsset(value) {
  if (value == null || value === '') {
    return { amount: undefined, asset: undefined };
  }

  const s = String(value).trim().replace(/,/g, '');

  // z.B. "0.0019009405USDT" oder "0.0019009405 USDT"
  const m = s.match(/^(-?\d+(?:\.\d+)?)(?:\s*([A-Za-z0-9._-]+))?$/);
  if (!m) {
    const fallback = num(s);
    return {
      amount: isNum(fallback) ? fallback : undefined,
      asset: undefined
    };
  }

  return {
    amount: num(m[1]),
    asset: m[2] ? normalizeSymbol(m[2]) : undefined
  };
}

function normalizeBinanceDate(v) {
  if (!v) return v;
  const s = String(v).trim();
  // "26-02-08 16:40:33" -> "2026-02-08 16:40:33"
  const m = s.match(/^(\d{2})-(\d{2})-(\d{2})(\s+.*)$/);
  if (m) return `20${m[1]}-${m[2]}-${m[3]}${m[4]}`;
  return s;
}

// Binance-spezifische Fee-Parsing (z.B. "35.0610000000THETA")
function parseBinanceFee(value) {
  if (value == null || value === '') return { amount: null, asset: null };
  const str = String(value).replace(/[",]/g, '').trim();
  const match = str.match(/^(-?[\d.]+)\s*([A-Za-z0-9]+)?$/);
  if (!match) return { amount: num(str), asset: null };
  return { amount: num(match[1]), asset: match[2] || null };
}

function parseBinanceUS(row, ctx){
  const ts = toUtc(row['Time'] || row['time'] || row['Date'] || row['Timestamp'], { exchange: 'binance_us' });
  if (!ts) return null;

  const userId =
    row['User ID'] ||
    row['user id'] ||
    row['UID'] ||
    row['uid'] ||
    '';
  const binanceUsTx = (params) =>
    normalTx({ exchangeAccountId: userId, ...params });

  const category = String(row['Category'] || row['category'] || '').trim().toLowerCase();
  const operation = String(row['Operation'] || row['operation'] || '').trim().toLowerCase();
  const orderId = row['Order ID'] || row['order id'] || '';
  const txId = row['Transaction ID'] || row['transaction id'] || orderId || '';
  const paymentMethod = row['Payment Method'] || row['payment method'] || '';
  const withdrawMethod = row['Withdraw Method'] || row['withdraw method'] || '';
  const additionalNote = row['Additional Note'] || row['additional note'] || '';

  const primaryAsset = String(row['Primary Asset'] || row['primary asset'] || '').trim().toUpperCase();
  const primaryAmount = num(row['Realized Amount For Primary Asset'] ?? row['Realized Amount for Primary Asset']);

  const baseAsset = String(row['Base Asset'] || row['base asset'] || '').trim().toUpperCase();
  const baseAmountRaw = num(row['Realized Amount For Base Asset'] ?? row['Realized Amount for Base Asset']);

  const quoteAsset = String(row['Quote Asset'] || row['quote asset'] || '').trim().toUpperCase();
  const quoteAmountRaw = num(row['Realized Amount for Quote Asset'] ?? row['Realized Amount For Quote Asset']);

  const feeAsset = String(row['Fee Asset'] || row['fee asset'] || '').trim().toUpperCase();
  const feeAmountRaw = num(row['Realized Amount for Fee Asset'] ?? row['Realized Amount For Fee Asset']);

  const normalizedOp = normalizeSide(operation) || operation;

  // ---- Trades (Binance.US: Buy/Sell mit Base/Quote in eigenem Schema) ----
  if ((category === 'buy' || category === 'sell' || normalizedOp === 'buy' || normalizedOp === 'sell') &&
      baseAsset && quoteAsset && isNum(baseAmountRaw) && isNum(quoteAmountRaw)) {
    const side = normalizedOp === 'sell' || category === 'sell' ? 'sell' : 'buy';

    // Binance.US Bedeutung:
    // - BUY  => Base Asset = spent asset (USD), Quote Asset = received asset (VET)
    // - SELL => Base Asset = sold asset (VET), Quote Asset = received asset (USD)
    const tradeBase = side === 'buy' ? quoteAsset : baseAsset;
    const tradeQuote = side === 'buy' ? baseAsset : quoteAsset;
    const tradeBaseAmount = side === 'buy' ? Math.abs(quoteAmountRaw) : Math.abs(baseAmountRaw);
    const tradeQuoteAmount = side === 'buy' ? Math.abs(baseAmountRaw) : Math.abs(quoteAmountRaw);
    const price = tradeBaseAmount ? round(tradeQuoteAmount / tradeBaseAmount) : undefined;

    return binanceUsTx({
      exchange:'binance_us', ctx, ts, type:'trade', side,
      base: tradeBase, baseAmount: tradeBaseAmount,
      quote: tradeQuote, quoteAmount: tradeQuoteAmount,
      price,
      feeAmount: isNum(feeAmountRaw) ? Math.abs(feeAmountRaw) : undefined,
      feeAsset: feeAsset || tradeQuote,
      txId,
      note: `Binance.US ${side}${paymentMethod ? ` (${paymentMethod})` : ''}${additionalNote ? ` - ${additionalNote}` : ''}`
    });
  }

  // ---- Rewards / Distributions ----
  if ((category === 'distribution' || operation.includes('reward') || operation.includes('distribution')) &&
      primaryAsset && isNum(primaryAmount)) {
    return binanceUsTx({
      exchange:'binance_us', ctx, ts, type:'income',
      base: primaryAsset, baseAmount: Math.abs(primaryAmount),
      txId,
      note: `Binance.US ${row['Operation'] || 'distribution'}${paymentMethod ? ` (${paymentMethod})` : ''}`
    });
  }

  // ---- Deposits ----
  if ((category === 'deposit' || operation.includes('deposit')) && primaryAsset && isNum(primaryAmount)) {
    return binanceUsTx({
      exchange:'binance_us', ctx, ts, type:'deposit',
      base: primaryAsset, baseAmount: Math.abs(primaryAmount),
      feeAmount: isNum(feeAmountRaw) ? Math.abs(feeAmountRaw) : undefined,
      feeAsset: feeAsset || primaryAsset,
      txId,
      note: `Binance.US deposit${paymentMethod ? ` (${paymentMethod})` : ''}${additionalNote ? ` - ${additionalNote}` : ''}`
    });
  }

  // ---- Withdrawals ----
  if ((category === 'withdrawal' || operation.includes('withdrawal') || operation.includes('withdraw')) && primaryAsset && isNum(primaryAmount)) {
    return binanceUsTx({
      exchange:'binance_us', ctx, ts, type:'withdrawal',
      base: primaryAsset, baseAmount: Math.abs(primaryAmount),
      feeAmount: isNum(feeAmountRaw) ? Math.abs(feeAmountRaw) : undefined,
      feeAsset: feeAsset || primaryAsset,
      txId,
      note: `Binance.US withdrawal${withdrawMethod ? ` (${withdrawMethod})` : ''}${additionalNote ? ` - ${additionalNote}` : ''}`
    });
  }

  return null;
}

function parseKraken(row, ctx){
  const ts = toUtc(row['time'], { exchange: 'kraken' });
  const [base, quote] = normalizeKrakenPair(row['pair']);
  const side = String(row['type'] || '').toLowerCase();
  const price = num(row['price']);
  const baseAmount = num(row['vol']);
  const quoteAmount = num(row['cost']);
  const fee = num(row['fee']);
  const txId = row['txid'];
  const orderId = row['ordertxid'];
  const orderType = row['ordertype'];
  const margin = num(row['margin']);
  const misc = row['misc'];
  const ledgers = row['ledgers'];

  // Kraken Trades-Export: alle Felder vorhanden
  if (row['pair'] && row['vol'] && row['cost']) {
    return {
      ...normalTx({
        exchange:'kraken', ctx, ts, type:'trade', side,
        base, baseAmount,
        quote, quoteAmount, price,
        feeAmount: fee, feeAsset: quote, // Fee in Quote-Währung
        txId, note: `Kraken ${orderType || side} ${misc ? `(${misc})` : ''}`
      }),
      importSource: 'trades',
    };
  }

  // Kraken Ledgers-Export: Asset-basierte Einträge
  if (row['asset'] && row['amount'] !== undefined) {
    const asset = String(row['asset'] || '').toUpperCase();
    const amount = num(row['amount']);
    const fee = num(row['fee']);
    const balance = num(row['balance']);
    const txType = String(row['type'] || '').toLowerCase();
    const subtype = String(row['subtype'] || '').toLowerCase();
    const aclass = String(row['aclass'] || '').toLowerCase();
    const refId = row['refid'] || row['refId'] || row['ref'] || null;
    const txId2 = row['txid'] || row['txId'] || null;

    // Nur Currency-Assets verarbeiten (aclass = 'currency')
    if (aclass !== 'currency') return null;

    // Ledger: trade erscheint als mehrere Legs (pro Asset eine Zeile), verknüpft über refid.
    // Daher NICHT direkt als "trade" importieren, sondern als "ledger_trade_leg" und später per refid paaren.
    if (txType === 'trade') {
      const leg = {
        ...normalTx({
          exchange: 'kraken', ctx, ts, type: 'ledger_trade_leg',
          base: asset, baseAmount: Math.abs(amount),
          feeAmount: fee, feeAsset: asset,
          txId: txId2,
          note: `Kraken ledger trade leg${subtype ? ` (${subtype})` : ''}`
        }),
        refId: refId || undefined,
        signedAmount: isNum(amount) ? amount : undefined,
        balance: isNum(balance) ? balance : undefined,
        importSource: 'ledger'
      };
      return leg;
    }

    // Deposit/Withdrawal/Income normal abbilden
    let type = 'deposit';
    if (txType === 'deposit' || txType === 'receive') type = 'deposit';
    else if (txType === 'withdrawal' || txType === 'spend') type = 'withdrawal';
    else if (txType === 'earn' || txType === 'staking') type = 'income';

    return {
      ...normalTx({
        exchange:'kraken', ctx, ts, type,
        base: asset, baseAmount: Math.abs(amount),
        feeAmount: fee, feeAsset: asset,
        txId: txId2,
        note: `Kraken ${txType}${subtype ? ` (${subtype})` : ''}`
      }),
      importSource: 'ledger',
    };
  }

  return null;
}

function parseCoinbase(row, ctx){
  const get = (...keys) => {
    for (const k of keys) {
      if (k in row && row[k] != null && String(row[k]).trim() !== '') return row[k];
    }
    return undefined;
  };

  // -------------- A) Coinbase "Transactions" Export --------------
  const txTypeRaw = get('Transaction Type','transaction type','Type','type');
  const hasTransactionsSchema = txTypeRaw != null;

  if (hasTransactionsSchema) {
    const t = String(txTypeRaw || '').toLowerCase().trim();
    const ts = toUtc(get('Timestamp','timestamp'));
    const txId = get('ID','Id','id');

    const asset = String(get('Asset','asset') || '').toUpperCase();
    const qty = num(get('Quantity Transacted','quantity transacted'));

    const fiat = String(get('Spot Price Currency','spot price currency','Price Currency','price currency') || '').toUpperCase();
    const spot = num(get('Spot Price at Transaction','spot price at transaction','Price at Transaction','price at transaction'));
    const subtotal = num(get('Subtotal','subtotal'));
    const total = num(get('Total (inclusive of fees and/or spread)','total (inclusive of fees and/or spread)','Total','total'));
    const feeDirect = num(get('Fees and/or Spread','fees and/or spread','Fees','fees'));
    const note = String(get('Notes','notes') || '');

    // Fee: bevorzugt explizite Spalte, sonst Total-Delta vs Subtotal
    const calculatedFee = isNum(feeDirect)
      ? Math.abs(feeDirect)
      : (isNum(total) && isNum(subtotal) ? Math.abs(total - subtotal) : undefined);
    const feeAsset = isNum(calculatedFee) && calculatedFee > 0 ? (fiat || undefined) : undefined;

    // BUY / SELL
    if (t === 'buy' || t === 'sell') {
      // Coinbase: "Subtotal" ist i.d.R. der Quote-Betrag ohne Fees/Spread.
      //         "Total" ist inkl. Fees/Spread.
      const quoteAmount = isNum(subtotal) ? Math.abs(subtotal) : (isNum(total) ? Math.abs(total) : undefined);

      return normalTx({
        exchange:'coinbase', ctx, ts, type:'trade', side: t,
        base: asset, baseAmount: isNum(qty) ? Math.abs(qty) : undefined,
        quote: fiat, quoteAmount,
        price: spot,
        feeAmount: calculatedFee,
        feeAsset,
        txId,
        note: `Coinbase ${t}${note ? ` | ${note}` : ''}`
      });
    }

    // SEND / RECEIVE / DEPOSIT / WITHDRAWAL
    if (t === 'send' || t === 'withdrawal') {
      return normalTx({
        exchange:'coinbase', ctx, ts, type:'withdrawal',
        base: asset, baseAmount: isNum(qty) ? Math.abs(qty) : undefined,
        feeAmount: calculatedFee,
        feeAsset,
        txId,
        note: `Coinbase ${t}${note ? ` | ${note}` : ''}`
      });
    }

    if (t === 'receive' || t === 'deposit') {
      return normalTx({
        exchange:'coinbase', ctx, ts, type:'deposit',
        base: asset, baseAmount: isNum(qty) ? Math.abs(qty) : undefined,
        txId,
        note: `Coinbase ${t}${note ? ` | ${note}` : ''}`
      });
    }

    // Rewards / Earn / Staking / Interest / Airdrop
    if (
      t.includes('reward') ||
      t.includes('earn') ||
      t.includes('staking') ||
      t.includes('interest') ||
      t.includes('airdrop')
    ) {
      return normalTx({
        exchange:'coinbase', ctx, ts, type:'income',
        base: asset, baseAmount: isNum(qty) ? Math.abs(qty) : undefined,
        txId,
        note: `Coinbase ${t}${note ? ` | ${note}` : ''}`
      });
    }

    // Convert / Swap: Crypto → Crypto Trade (kein Fiat-Split!)
    if (t === 'convert' || t === 'swap') {
      // Beispiel Notes: "Converted 518.9094 LTC to 6.65444836 BTC"
      const m = note.match(/converted\s+([\d.,\-()]+)\s+([A-Za-z0-9]+)\s+to\s+([\d.,\-()]+)\s+([A-Za-z0-9]+)/i);
      if (m) {
        const fromAmount = num(m[1]);
        const fromAsset = String(m[2] || '').toUpperCase();
        const toAmount = num(m[3]);
        const toAsset = String(m[4] || '').toUpperCase();
        const price = (isNum(fromAmount) && isNum(toAmount) && toAmount !== 0)
          ? Math.abs(fromAmount) / Math.abs(toAmount)
          : undefined;

        return normalTx({
          exchange:'coinbase', ctx, ts, type:'trade', side:'buy',
          base: toAsset, baseAmount: isNum(toAmount) ? Math.abs(toAmount) : undefined,
          quote: fromAsset, quoteAmount: isNum(fromAmount) ? Math.abs(fromAmount) : undefined,
          price,
          feeAmount: calculatedFee,
          feeAsset,
          txId,
          note: `Coinbase ${t}${note ? ` | ${note}` : ''}`
        });
      }

      // Fallback: falls Notes nicht parsebar sind
      return normalTx({
        exchange:'coinbase', ctx, ts, type:'trade', side:'unknown',
        base: asset, baseAmount: isNum(qty) ? Math.abs(qty) : undefined,
        feeAmount: calculatedFee,
        feeAsset,
        txId,
        note: `Coinbase ${t}${note ? ` | ${note}` : ''}`
      });
    }

    // Advanced Trade Fill (manchmal als Transaction Type enthalten)
    if (t.includes('advanced trade')) {
      const quoteAmount = isNum(subtotal) ? Math.abs(subtotal) : (isNum(total) ? Math.abs(total) : undefined);
      return normalTx({
        exchange:'coinbase', ctx, ts, type:'trade', side:'unknown',
        base: asset, baseAmount: isNum(qty) ? Math.abs(qty) : undefined,
        quote: fiat, quoteAmount,
        price: spot,
        feeAmount: calculatedFee,
        feeAsset,
        txId,
        note: `Coinbase ${t}${note ? ` | ${note}` : ''}`
      });
    }

    // Sonstige Typen: best effort
    return normalTx({
      exchange:'coinbase', ctx, ts, type:'trade', side:'unknown',
      base: asset, baseAmount: isNum(qty) ? Math.abs(qty) : undefined,
      quote: fiat || undefined,
      quoteAmount: isNum(subtotal) ? Math.abs(subtotal) : undefined,
      price: spot,
      feeAmount: calculatedFee,
      feeAsset,
      txId,
      note: `Coinbase ${t}${note ? ` | ${note}` : ''}`
    });
  }

  // -------------- B) Coinbase Advanced Trade (Fills / Trades) --------------
  const pair = String(get('Product','product','Market','market','Pair','pair','Symbol','symbol') || '').toUpperCase();
  const side = normalizeSide(get('Side','side'));
  const price = num(get('Price','price'));
  const baseAmount = num(get('Size','size','Filled','filled','Amount','amount','Qty','qty'));
  const feeAmount = num(get('Fee','fee'));
  const ts = toUtc(get('Created At','created at','Timestamp','timestamp','Time','time'));
  const txId = get('Trade ID','trade id','ID','Id','id');

  if (pair && side && isNum(price) && isNum(baseAmount)) {
    const [base, quote] = splitPair(pair);
    const quoteAmount = Math.abs(price * baseAmount);
    return normalTx({
      exchange:'coinbase', ctx, ts, type:'trade', side,
      base, baseAmount: Math.abs(baseAmount),
      quote, quoteAmount,
      price,
      feeAmount: isNum(feeAmount) ? Math.abs(feeAmount) : undefined,
      feeAsset: quote,
      txId,
      note: 'Coinbase Advanced Trade'
    });
  }

  return null;
}

function parseBitpanda(row, ctx){
  const tsCol = row['Timestamp'] || row['Date'] || row['Datum'];
  const tsParsed = parseExchangeInstant(tsCol, { exchange: 'bitpanda' });
  const ts = tsParsed.iso;
  ctx = { ...ctx, exchangeTimeInterpretation: tsParsed.interpretation };
  
  // --- NEU: In/Out-Schema (Bitpanda "Transactions" Export) ---
  const hasInOut =
    (row['Amount In'] || row['Amount in'] || row['Receive']) !== undefined &&
    (row['Amount Out'] || row['Amount out'] || row['Spend']) !== undefined &&
    (row['Currency In'] || row['Currency in']) !== undefined &&
    (row['Currency Out'] || row['Currency out'] || row['Currency']) !== undefined;

  if (hasInOut) {
    const amtIn  = num(row['Amount In']  ?? row['Amount in']  ?? row['Receive']);
    const amtOut = num(row['Amount Out'] ?? row['Amount out'] ?? row['Spend']);
    const curIn  = normalizeSymbol(row['Currency In']  ?? row['Currency in']);
    const curOut = normalizeSymbol(row['Currency Out'] ?? row['Currency out'] ?? row['Currency']);
    const fee    = num(row['Fee']);
    const feeCur = normalizeSymbol(row['Fee asset'] || row['Fee Asset'] || row['Fee currency']);

    // Heuristik: BUY wenn In = VeChain-Token & Out = Fiat; SELL wenn umgekehrt.
    const inIsVe  = isWhitelisted(curIn);
    const outIsVe = isWhitelisted(curOut);
    const outIsFiat = FIAT.has(curOut);
    const inIsFiat  = FIAT.has(curIn);

    if (inIsVe && (outIsFiat || !outIsVe)) {
      // BUY: wir bekommen VET/B3TR/... und zahlen EUR/USDT
      const price = (isNum(amtIn) && isNum(amtOut)) ? Math.abs(amtOut) / Math.abs(amtIn) : undefined;
      return normalTx({
        exchange:'bitpanda', ctx, ts, type:'trade', side:'buy',
        base: curIn,  baseAmount: Math.abs(amtIn),
        quote: curOut, quoteAmount: isNum(amtOut) ? Math.abs(amtOut) : undefined,
        price, feeAmount: fee, feeAsset: feeCur || (outIsFiat ? curOut : undefined),
        txId: row['Transaction ID'] || row['TxID'], note:'bitpanda_inout'
      });
    }

    if (outIsVe && (inIsFiat || !inIsVe)) {
      // SELL: wir geben VET/B3TR/... ab und erhalten EUR/USDT
      const price = (isNum(amtIn) && isNum(amtOut)) ? Math.abs(amtIn) / Math.abs(amtOut) : undefined;
      return normalTx({
        exchange:'bitpanda', ctx, ts, type:'trade', side:'sell',
        base: curOut, baseAmount: Math.abs(amtOut),
        quote: curIn,  quoteAmount: isNum(amtIn) ? Math.abs(amtIn) : undefined,
        price, feeAmount: fee, feeAsset: feeCur || (inIsFiat ? curIn : undefined),
        txId: row['Transaction ID'] || row['TxID'], note:'bitpanda_inout'
      });
    }
    // Falls beides Krypto (Swap): als Trade ohne Side, Postprocessing kann später paaren
    if (inIsVe || outIsVe) {
      return normalTx({
        exchange:'bitpanda', ctx, ts, type:'trade', side: undefined,
        base: inIsVe ? curIn : curOut,
        baseAmount: inIsVe ? Math.abs(amtIn) : Math.abs(amtOut),
        quote: inIsVe ? curOut : curIn,
        price: undefined, feeAmount: fee, feeAsset: feeCur,
        txId: row['Transaction ID'] || row['TxID'], note:'bitpanda_inout_swap'
      });
    }
    // ansonsten weiter unten in die alte Logik/Fallback
  }

  // --- bestehende (alte) Bitpanda-Logik: "Transaction Type / Amount Asset / Fiat" ---
  const txType = String(row['Transaction Type'] || row['Type'] || '').toLowerCase();
  const assetClass = String(row['Asset Class'] || row['Asset class'] || '').toLowerCase();
  
  // ✅ Akzeptiere gängige Krypto-Bezeichnungen (EN/DE); skippe nur Nicht-Krypto
  const cryptoSynonyms = ['crypto','cryptocurrency','cryptocoin','krypto','kryptowährung'];
  if (assetClass && !cryptoSynonyms.some(x => assetClass.includes(x))) {
    // Erlaubte Nicht-Krypto: falls du Metalle/Aktien explizit NICHT importieren willst
    const nonCrypto = ['metal','metall','stock','aktie','etf','index'];
    if (nonCrypto.some(x => assetClass.includes(x))) return null;
    // Unbekannte Klassen lassen wir durch (defensiv), weil Bitpanda hier gerne variiert.
  }

  const asset = (row['Asset'] || '').toUpperCase();
  const assetAmount = num(row['Amount Asset'] || row['Asset Amount'] || row['Crypto Amount']);
  const fiatCcy = (row['Fiat'] || row['Fiat currency'] || row['Asset market price currency'] || '').toUpperCase();
  const fiatAmount = num(row['Amount Fiat'] || row['Fiat Amount']);
  const fee = num(row['Fee']);
  const feeAsset = (row['Fee asset'] || row['Fee Asset'] || fiatCcy || '').toUpperCase();

  // Falls Bitpanda DE: "Kauf" / "Verkauf"
  const isBuy  = txType.includes('buy') || txType.includes('kauf');
  const isSell = txType.includes('sell') || txType.includes('verkauf');
  const isSwap = txType.includes('swap') || txType.includes('convert') || txType.includes('tausch');

  if (isBuy) {
    // Kauf: +Asset, -Fiat
    return normalTx({
      exchange:'bitpanda', ctx, ts, type:'trade', side:'buy',
      base: asset, baseAmount: assetAmount,
      quote: fiatCcy, quoteAmount: Math.abs(fiatAmount) || (assetAmount && num(row['Asset market price']) ? assetAmount * num(row['Asset market price']) : undefined),
      price: (assetAmount && fiatAmount) ? Math.abs(fiatAmount) / assetAmount : num(row['Asset market price']),
      feeAmount: fee, feeAsset: feeAsset || fiatCcy,
      txId: row['Transaction ID'] || row['TxID'], note: row['Transaction Type'] || row['Type']
    });
  }
  if (isSell) {
    // Verkauf: -Asset, +Fiat
    return normalTx({
      exchange:'bitpanda', ctx, ts, type:'trade', side:'sell',
      base: asset, baseAmount: assetAmount,
      quote: fiatCcy, quoteAmount: Math.abs(fiatAmount) || (assetAmount && num(row['Asset market price']) ? assetAmount * num(row['Asset market price']) : undefined),
      price: (assetAmount && fiatAmount) ? Math.abs(fiatAmount) / assetAmount : num(row['Asset market price']),
      feeAmount: fee, feeAsset: feeAsset || fiatCcy,
      txId: row['Transaction ID'] || row['TxID'], note: row['Transaction Type'] || row['Type']
    });
  }
  if (isSwap) {
    // Swap: Bitpanda liefert hier meist nur die Asset-Seite + Preis in fiat_ccy.
    // Markiere als 'convert_raw', das Post-Processing paart zwei Rows (out/in) zusammen.
    return normalTx({
      exchange:'bitpanda', ctx, ts, type:'trade', side: undefined,
      base: asset, baseAmount: assetAmount,
      quote: fiatCcy, quoteAmount: undefined,
      price: num(row['Asset market price']),
      feeAmount: fee, feeAsset: feeAsset,
      txId: row['Transaction ID'], note: 'swap_raw'
    });
  }

  // Deposits/Withdrawals/Rewards:
  const t = txType;
  if (t.includes('deposit') || t.includes('einzahlung'))
    return normalTx({ exchange:'bitpanda', ctx, ts, type:'deposit', base: asset || fiatCcy, baseAmount: assetAmount || fiatAmount, txId: row['Transaction ID'] });
  if (t.includes('withdraw') || t.includes('auszahlung'))
    return normalTx({ exchange:'bitpanda', ctx, ts, type:'withdrawal', base: asset || fiatCcy, baseAmount: assetAmount || fiatAmount, feeAmount: fee, feeAsset, txId: row['Transaction ID'] });
  if (t.includes('reward') || t.includes('staking') || t.includes('bonus'))
    return normalTx({ exchange:'bitpanda', ctx, ts, type:'income', base: asset, baseAmount: assetAmount, txId: row['Transaction ID'], note: txType });

  // Fallback: ignorieren wir nicht – gib lieber Rohdaten zurück zur manuellen Sicht
  return normalTx({ exchange:'bitpanda', ctx, ts, type:'trade', base: asset, baseAmount: assetAmount, quote: fiatCcy, note: `unmapped:${txType}` });
}

function parseBybit(row, ctx){
  const ts = toUtc(row['Trade time'] || row['Filled Time'] || row['Date(UTC)'] || row['Date']);
  const pair = (row['Symbol'] || row['Spot Pair'] || row['Pair'] || '').toUpperCase();
  const [base, quote] = splitPair(pair);
  const side = String(row['Side'] || row['Direction'] || '').toLowerCase();
  const price = num(row['Filled Price'] || row['Price']);
  const baseAmount = num(row['Filled Qty'] || row['Filled Quantity']);
  const total = num(row['Filled Value']);
  const fee = num(row['Fee Paid'] || row['Fees']);
  const feeAsset = String(row['Fee Asset'] || '').toUpperCase();
  const txId = row['Order No.'] || row['Transaction ID'] || row['Order ID'] || '';
  const orderType = row['Order Type'] || '';

  // Trade History Export (bevorzugt)
  if (row['Symbol'] && row['Side'] && (row['Filled Qty'] || row['Filled Quantity'])) {
    return normalTx({
      exchange: 'bybit', ctx, ts, type: 'trade', side,
      base, baseAmount,
      quote, quoteAmount: total || round(price * baseAmount), price,
      feeAmount: fee, feeAsset: feeAsset || quote,
      txId, note: `Bybit ${orderType || side} ${baseAmount} ${base}`
    });
  }

  // Asset Change Details Export
  const type = String(row['Type'] || '').toLowerCase();
  const asset = String(row['Coin'] || row['Asset'] || '').toUpperCase();
  const amount = num(row['Amount'] || row['Change']);
  const description = row['Description'] || row['Remark'] || '';

  if (asset && amount !== undefined) {
    // Bestimme Transaktionstyp basierend auf Bybit-Type
    let txType = 'deposit';
    if (type === 'trade') txType = 'trade';
    else if (type === 'tradingfee') txType = 'fee';
    else if (type === 'deposit') txType = 'deposit';
    else if (type === 'withdraw') txType = 'withdrawal';
    else if (type === 'rebate') txType = 'income';
    else if (type === 'commission') txType = 'income';
    else if (type === 'interest') txType = 'income';
    else if (type === 'bonus') txType = 'income';
    else if (type === 'airdrop') txType = 'income';

    return normalTx({
      exchange: 'bybit', ctx, ts, type: txType,
      base: asset, baseAmount: Math.abs(amount),
      feeAmount: type === 'tradingfee' ? Math.abs(amount) : undefined,
      feeAsset: type === 'tradingfee' ? asset : undefined,
      txId, note: `Bybit ${type} ${description}`
    });
  }

  return null;
}

function parseGateIo(row, ctx){
  const ts = toUtc(row['Date'] || row['Time']);
  const pair = (row['Market'] || row['Pair'] || '').toUpperCase().replace('_', '/');
  const [base, quote] = splitPair(pair);
  const side = String(row['Type'] || row['Trade type'] || row['Side'] || '').toLowerCase();
  const price = num(row['Price']);
  const baseAmount = num(row['Amount']);
  const total = num(row['Total']);
  const fee = num(row['Fee']);
  const feeCoin = String(row['Fee Coin'] || '').toUpperCase();
  const tradeNo = row['No'] || row['Trade ID'] || '';

  // Spot Trade History Export
  if (row['Market'] && row['Type'] && row['Price']) {
    return normalTx({
      exchange: 'gateio', ctx, ts, type: 'trade', side,
      base, baseAmount,
      quote, quoteAmount: total || round(price * baseAmount), price,
      feeAmount: fee, feeAsset: feeCoin || quote,
      txId: tradeNo, note: `Gate.io ${side} ${baseAmount} ${base}`
    });
  }

  // Deposit Export
  const depositCoin = String(row['Coin'] || '').toUpperCase();
  const depositAmount = num(row['Amount']);
  const depositTxId = row['TxID'] || row['Transaction ID'] || '';
  const depositStatus = row['Status'] || '';
  const depositNetwork = row['Network'] || '';

  if (row['Coin'] && row['Amount'] && (row['TxID'] || row['Transaction ID'])) {
    return normalTx({
      exchange: 'gateio', ctx, ts, type: 'deposit',
      base: depositCoin, baseAmount: Math.abs(depositAmount),
      txId: depositTxId, note: `Gate.io deposit ${depositNetwork} ${depositStatus}`
    });
  }

  // Withdrawal Export
  if (row['Coin'] && row['Amount'] && (row['TxID'] || row['Transaction ID']) && Math.abs(depositAmount) !== depositAmount) {
    return normalTx({
      exchange: 'gateio', ctx, ts, type: 'withdrawal',
      base: depositCoin, baseAmount: Math.abs(depositAmount),
      feeAmount: fee, feeAsset: depositCoin,
      txId: depositTxId, note: `Gate.io withdrawal ${depositNetwork} ${depositStatus}`
    });
  }

  // Margin Trade Export (zusätzliche Felder)
  const marginType = row['Margin Type'] || '';
  const fundingCost = num(row['Funding Cost'] || row['Funding']);
  const liquidation = row['Liquidation'] || '';

  if (marginType && row['Market']) {
    return normalTx({
      exchange: 'gateio', ctx, ts, type: 'trade', side,
      base, baseAmount,
      quote, quoteAmount: total || round(price * baseAmount), price,
      feeAmount: fee + (fundingCost || 0), feeAsset: feeCoin || quote,
      txId: tradeNo, note: `Gate.io margin ${side} ${liquidation ? '(liquidation)' : ''}`
    });
  }

  // Rewards / Earn / Staking / Interest
  const gateKindRaw = String(
    row['Type'] || row['Category'] || row['Operation'] || row['Action'] || ''
  ).toLowerCase().trim();

  const gateAsset = String(row['Coin'] || row['Currency'] || '').toUpperCase();
  const gateAmount = num(row['Amount'] || row['Change'] || row['Quantity']);
  const gateTxId = row['TxID'] || row['Transaction ID'] || row['ID'] || '';

  if (
    !row['Market'] &&
    !row['Pair'] &&
    gateAsset &&
    isNum(gateAmount) &&
    (
      gateKindRaw.includes('reward') ||
      gateKindRaw.includes('staking') ||
      gateKindRaw.includes('earn') ||
      gateKindRaw.includes('interest') ||
      gateKindRaw.includes('airdrop')
    )
  ) {
    return normalTx({
      exchange: 'gateio', ctx, ts, type: 'income',
      base: gateAsset, baseAmount: Math.abs(gateAmount),
      txId: gateTxId,
      note: `Gate.io ${gateKindRaw || 'income'}`
    });
  }

  return null;
}

function parseMexc(row, ctx) {
  const get = (...keys) => {
    for (const k of keys) {
      if (row[k] != null && String(row[k]).trim() !== '') return row[k];
    }
    return undefined;
  };

  const ts = toUtc(get('Time', 'Date', 'Timestamp', 'Created Time', 'Completed Time'));
  const pairRaw = String(get('Pairs', 'Pair', 'Symbol', 'Market') || '').toUpperCase();
  const pair = pairRaw.replace(/_/g, '/');
  const [pairBase, pairQuote] = splitPair(pair);

  const txRef = get(
    'Order ID',
    'Trade ID',
    'ID',
    'Transaction ID',
    'TxID',
    'Hash',
    'Withdraw ID',
    'Deposit ID'
  );

  // 1) Spot Trade History
  if (
    get('Pairs') !== undefined &&
    get('Side') !== undefined &&
    (get('Filled Price') !== undefined || get('Executed Amount') !== undefined)
  ) {
    const side = normalizeSide(get('Side'));
    const price = num(get('Filled Price', 'Price'));
    const baseAmount = num(get('Executed Amount', 'Filled Amount', 'Amount'));
    const quoteAmount =
      num(get('Total', 'Executed Value', 'Turnover')) ||
      (isNum(price) && isNum(baseAmount) ? round(price * baseAmount) : undefined);

    const fee = num(get('Fee'));
    // MEXC Spot Fee-Regel: BUY -> Base, SELL -> Quote
    const feeAsset = side === 'buy' ? pairBase : side === 'sell' ? pairQuote : undefined;
    const role = String(get('Role') || '').toLowerCase();

    if (!ts || !pairBase || !pairQuote || !side || !isNum(price) || !isNum(baseAmount)) {
      return null;
    }

    return {
      ...mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'trade',
        side,
        base: pairBase,
        baseAmount: Math.abs(baseAmount),
        quote: pairQuote,
        quoteAmount: isNum(quoteAmount) ? Math.abs(quoteAmount) : undefined,
        price,
        feeAmount: isNum(fee) ? Math.abs(fee) : undefined,
        feeAsset,
        txId: txRef,
        note: `MEXC trade history${role ? ` (${role})` : ''}`
      }),
      importSource: 'trade_history'
    };
  }

  // 2) Spot Order History
  if (
    get('Pairs') !== undefined &&
    get('Direction') !== undefined &&
    (
      get('Average Filled Price') !== undefined ||
      get('Filled Quantity') !== undefined ||
      get('Status') !== undefined
    )
  ) {
    const side = normalizeSide(get('Direction', 'Side'));
    const status = String(get('Status') || '').toLowerCase();
    const avgFilledPrice = num(get('Average Filled Price', 'Filled Price'));
    const filledQty = num(get('Filled Quantity', 'Executed Amount', 'Executed Quantity'));
    const orderAmount = num(get('Order Amount', 'Total'));

    if (!isNum(filledQty) || filledQty <= 0) return null;
    if (
      status &&
      (
        status.includes('canceled') ||
        status.includes('cancelled') ||
        status.includes('open') ||
        status.includes('new') ||
        status.includes('pending')
      ) &&
      !status.includes('partial')
    ) {
      return null;
    }

    if (!ts || !pairBase || !pairQuote || !side || !isNum(avgFilledPrice)) {
      return null;
    }

    return {
      ...mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'trade',
        side,
        base: pairBase,
        baseAmount: Math.abs(filledQty),
        quote: pairQuote,
        quoteAmount:
          isNum(orderAmount) ? Math.abs(orderAmount) : round(avgFilledPrice * Math.abs(filledQty)),
        price: avgFilledPrice,
        txId: txRef,
        note: `MEXC order history ${String(get('Type') || '').toLowerCase()} ${status}`.trim()
      }),
      importSource: 'order_history'
    };
  }

  // 3) Convert / MX Conversion / In-Out Statement
  const curIn = normalizeSymbol(get('Currency In', 'To Currency', 'Receive Currency'));
  const amtIn = num(get('Amount In', 'To Amount', 'Receive Amount'));
  const curOut = normalizeSymbol(get('Currency Out', 'From Currency', 'Pay Currency', 'Currency'));
  const amtOut = num(get('Amount Out', 'From Amount', 'Pay Amount', 'Amount'));

  if (ts && curIn && curOut && isNum(amtIn) && isNum(amtOut) && curIn !== curOut) {
    const receiveIsWhite = isWhitelisted(curIn);
    const spendIsWhite = isWhitelisted(curOut);
    const receiveIsFiat = FIAT.has(curIn);
    const spendIsFiat = FIAT.has(curOut);

    let side = 'buy';
    let base = curIn;
    let baseAmount = Math.abs(amtIn);
    let quote = curOut;
    let quoteAmount = Math.abs(amtOut);

    if (spendIsWhite && receiveIsFiat) {
      side = 'sell';
      base = curOut;
      baseAmount = Math.abs(amtOut);
      quote = curIn;
      quoteAmount = Math.abs(amtIn);
    } else if (!(receiveIsWhite && spendIsFiat)) {
      side = 'buy';
      base = curIn;
      baseAmount = Math.abs(amtIn);
      quote = curOut;
      quoteAmount = Math.abs(amtOut);
    }

    const price = isNum(baseAmount) && baseAmount > 0 ? round(quoteAmount / baseAmount) : undefined;

    return {
      ...mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'trade',
        side,
        base,
        baseAmount,
        quote,
        quoteAmount,
        price,
        txId: txRef,
        note: 'MEXC convert'
      }),
      importSource: 'statement_convert'
    };
  }

  // 4) Funding History / Spot Statement / Events / Savings
  const typeRaw = String(
    get(
      'Transaction Type',
      'Type',
      'Record Type',
      'Category',
      'Operation',
      'Action',
      'Description',
      'Transaction Kind'
    ) || ''
  ).toLowerCase().trim();

  const directionRaw = String(get('Direction', 'In/Out', 'In Out', 'Flow') || '')
    .toLowerCase()
    .trim();

  const asset = normalizeSymbol(get('Crypto', 'Coin', 'Currency', 'Asset'));
  const amount = num(get('Amount', 'Quantity', 'Change', 'Net Amount'));
  const network = get('Network', 'Chain');
  const address = get('Address', 'Wallet Address', 'Withdrawal Address', 'Deposit Address');
  const status = String(get('Status') || '').trim().toLowerCase();
  const fee = num(get('Network Fee', 'Fee', 'Withdrawal Fee'));
  const feeAsset = normalizeSymbol(get('Fee Asset')) || asset;

  const isIncome =
    typeRaw.includes('reward') ||
    typeRaw.includes('staking') ||
    typeRaw.includes('earn') ||
    typeRaw.includes('interest') ||
    typeRaw.includes('airdrop') ||
    typeRaw.includes('saving') ||
    typeRaw.includes('savings') ||
    typeRaw.includes('launchpool') ||
    typeRaw.includes('kickstarter') ||
    typeRaw.includes('rebate') ||
    typeRaw.includes('bonus') ||
    typeRaw.includes('referral') ||
    typeRaw.includes('commission');

  const isDeposit =
    typeRaw.includes('deposit') ||
    directionRaw === 'in' ||
    directionRaw === 'deposit';

  const isWithdrawal =
    typeRaw.includes('withdraw') ||
    typeRaw.includes('send') ||
    directionRaw === 'out' ||
    directionRaw === 'withdrawal';

  const isTransfer =
    typeRaw.includes('transfer') ||
    typeRaw.includes('internal transfer');

  if (ts && asset && isNum(amount)) {
    if (isIncome) {
      return mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'income',
        base: asset,
        baseAmount: Math.abs(amount),
        txId: txRef,
        note: `MEXC ${typeRaw || 'income'}`
      });
    }

    if (isDeposit && !isWithdrawal) {
      return mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'deposit',
        base: asset,
        baseAmount: Math.abs(amount),
        txId: txRef,
        note: `MEXC deposit${network ? ` (${network})` : ''}${status ? ` - ${status}` : ''}${address ? ` -> ${address}` : ''}`
      });
    }

    if (isWithdrawal) {
      return mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'withdrawal',
        base: asset,
        baseAmount: Math.abs(amount),
        feeAmount: isNum(fee) ? Math.abs(fee) : undefined,
        feeAsset: isNum(fee) ? (feeAsset || asset) : undefined,
        txId: txRef,
        note: `MEXC withdrawal${network ? ` (${network})` : ''}${status ? ` - ${status}` : ''}${address ? ` -> ${address}` : ''}`
      });
    }

    if (isTransfer) {
      return mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'transfer',
        base: asset,
        baseAmount: Math.abs(amount),
        txId: txRef,
        note: `MEXC transfer${typeRaw ? ` ${typeRaw}` : ''}`
      });
    }
  }

  return null;
}

// ===============================
// MEXC FIX (trade_history bevorzugen, fee korrekt, deposit/withdraw robuster)
// Implementiert als separate Funktion, damit wir den alten Parser nicht riskant überschreiben.
// ===============================
function parseMexcFixed(row, ctx) {
  const get = (...keys) => {
    for (const k of keys) {
      if (row[k] != null && String(row[k]).trim() !== '') return row[k];
    }
    return undefined;
  };

  const exchangeAccountId = get('UID');
  const mexcTx = (params) => normalTx({ exchangeAccountId, ...params });

  const fileName = String(ctx?.file || '').toLowerCase();

  const ts = toUtc(
    get(
      'Time',
      'Date',
      'Timestamp',
      'Created Time',
      'Completed Time',
      'Apply Time',
      'Update Time',
      'Deposit Time',
      'Withdrawal Time',
      'Transfer Time'
    )
  );

  const pairRaw = String(get('Pairs', 'Pair', 'Symbol', 'Market') || '').toUpperCase();
  const pair = pairRaw.replace(/_/g, '/');
  const [pairBase, pairQuote] = splitPair(pair);

  const syntheticRef = [
    get('UID'),
    pairRaw,
    get('Time', 'Date', 'Timestamp', 'Created Time', 'Completed Time'),
    get('Side', 'Direction'),
    get('Filled Price', 'Average Filled Price', 'Price'),
    get('Executed Amount', 'Filled Quantity', 'Amount'),
    get('Total', 'Order Amount')
  ].filter(Boolean).join('|');

  const txRef =
    get(
      'Order ID',
      'Trade ID',
      'ID',
      'Transaction ID',
      'TxID',
      'Hash',
      'Transaction Hash',
      'Withdraw ID',
      'Deposit ID'
    ) || syntheticRef || undefined;

  // -------------------------------------------------
  // 1) Spot Trade History
  // UID | Pairs | Time | Side | Filled Price | Executed Amount | Total | Fee | Role
  // -------------------------------------------------
  if (
    get('Pairs') !== undefined &&
    get('Side') !== undefined &&
    (get('Filled Price') !== undefined || get('Executed Amount') !== undefined)
  ) {
    const side = normalizeSide(get('Side'));
    const price = num(get('Filled Price', 'Price'));
    const baseAmount = num(get('Executed Amount', 'Filled Amount', 'Amount'));
    const quoteAmount =
      num(get('Total', 'Executed Value', 'Turnover')) ||
      (isNum(price) && isNum(baseAmount) ? round(price * baseAmount) : undefined);

    const feeParsed = parseAmountWithAsset(get('Fee'));
    const role = String(get('Role') || '').toLowerCase();

    if (!ts || !pairBase || !pairQuote || !side || !isNum(price) || !isNum(baseAmount)) {
      return null;
    }

    return {
      ...mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'trade',
        side,
        base: pairBase,
        baseAmount: Math.abs(baseAmount),
        quote: pairQuote,
        quoteAmount: isNum(quoteAmount) ? Math.abs(quoteAmount) : undefined,
        price,
        feeAmount: isNum(feeParsed.amount) ? Math.abs(feeParsed.amount) : undefined,
        feeAsset: feeParsed.asset || pairQuote,
        txId: txRef,
        note: `MEXC trade history${role ? ` (${role})` : ''}`
      }),
      importSource: 'trade_history'
    };
  }

  // -------------------------------------------------
  // 2) Spot Order History
  // UID | Pairs | Time | Type | Direction | Average Filled Price | ...
  // -------------------------------------------------
  if (
    get('Pairs') !== undefined &&
    get('Direction') !== undefined &&
    (
      get('Average Filled Price') !== undefined ||
      get('Filled Quantity') !== undefined ||
      get('Status') !== undefined
    )
  ) {
    const side = normalizeSide(get('Direction', 'Side'));
    const status = String(get('Status') || '').toLowerCase();
    const avgFilledPrice = num(get('Average Filled Price', 'Filled Price'));
    const filledQty = num(get('Filled Quantity', 'Executed Amount', 'Executed Quantity'));
    const orderAmount = num(get('Order Amount', 'Total'));

    if (!isNum(filledQty) || filledQty <= 0) return null;

    if (
      status &&
      (
        status.includes('canceled') ||
        status.includes('cancelled') ||
        status.includes('open') ||
        status.includes('new') ||
        status.includes('pending')
      ) &&
      !status.includes('partial') &&
      !status.includes('filled')
    ) {
      return null;
    }

    if (!ts || !pairBase || !pairQuote || !side || !isNum(avgFilledPrice)) {
      return null;
    }

    return {
      ...mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'trade',
        side,
        base: pairBase,
        baseAmount: Math.abs(filledQty),
        quote: pairQuote,
        quoteAmount:
          isNum(orderAmount) ? Math.abs(orderAmount) : round(avgFilledPrice * Math.abs(filledQty)),
        price: avgFilledPrice,
        txId: txRef,
        note: `MEXC order history ${String(get('Type') || '').toLowerCase()} ${status}`.trim()
      }),
      importSource: 'order_history'
    };
  }

  // -------------------------------------------------
  // 3) Convert / Small Asset / Statement
  // -------------------------------------------------
  const curIn = normalizeSymbol(get('Currency In', 'To Currency', 'Receive Currency'));
  const amtIn = num(get('Amount In', 'To Amount', 'Receive Amount'));
  const curOut = normalizeSymbol(get('Currency Out', 'From Currency', 'Pay Currency', 'Currency'));
  const amtOut = num(get('Amount Out', 'From Amount', 'Pay Amount', 'Amount'));

  if (ts && curIn && curOut && isNum(amtIn) && isNum(amtOut) && curIn !== curOut) {
    const receiveIsWhite = isWhitelisted(curIn);
    const spendIsWhite = isWhitelisted(curOut);
    const receiveIsFiat = FIAT.has(curIn);
    const spendIsFiat = FIAT.has(curOut);

    let side = 'buy';
    let base = curIn;
    let baseAmount = Math.abs(amtIn);
    let quote = curOut;
    let quoteAmount = Math.abs(amtOut);

    if (spendIsWhite && receiveIsFiat) {
      side = 'sell';
      base = curOut;
      baseAmount = Math.abs(amtOut);
      quote = curIn;
      quoteAmount = Math.abs(amtIn);
    } else if (!(receiveIsWhite && spendIsFiat)) {
      side = 'buy';
      base = curIn;
      baseAmount = Math.abs(amtIn);
      quote = curOut;
      quoteAmount = Math.abs(amtOut);
    }

    const price = isNum(baseAmount) && baseAmount > 0 ? round(quoteAmount / baseAmount) : undefined;

    return {
      ...mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'trade',
        side,
        base,
        baseAmount,
        quote,
        quoteAmount,
        price,
        txId: txRef,
        note: 'MEXC convert'
      }),
      importSource: 'statement_convert'
    };
  }

  // -------------------------------------------------
  // 4) Funding / Deposit / Withdrawal / Income
  // -------------------------------------------------
  const typeRaw = String(
    get(
      'Transaction Type',
      'Type',
      'Record Type',
      'Category',
      'Operation',
      'Action',
      'Description',
      'Transaction Kind'
    ) || ''
  ).toLowerCase().trim();

  const directionRaw = String(get('Direction', 'In/Out', 'In Out', 'Flow') || '')
    .toLowerCase()
    .trim();

  const asset = normalizeSymbol(get('Crypto', 'Coin', 'Currency', 'Asset', 'Token'));
  const network = get('Network', 'Chain', 'Chain Name');
  const address = get(
    'Address',
    'Wallet Address',
    'Withdrawal Address',
    'Deposit Address',
    'To Address'
  );
  const memo = get('memo', 'Memo', 'Tag');
  const progress = get('Progress');
  const status = String(get('Status') || '').trim().toLowerCase();

  // echte MEXC Funding-Felder
  const depositAmount = num(get('Deposit Amount'));
  const requestAmount = num(get('Request Amount'));
  const settlementAmount = num(get('Settlement Amount'));
  const genericAmount = num(
    get(
      'Amount',
      'Quantity',
      'Change',
      'Net Amount',
      'Actual Amount',
      'Receive Amount',
      'Withdrawal Amount'
    )
  );

  // Für Deposits ist "Deposit Amount" maßgeblich.
  const resolvedDepositAmount =
    isNum(depositAmount) ? depositAmount : genericAmount;

  // Für Withdrawals ist "Settlement Amount" maßgeblich (on-chain empfangen).
  const resolvedWithdrawalAmount =
    isNum(settlementAmount)
      ? settlementAmount
      : (isNum(requestAmount) ? requestAmount : genericAmount);

  const tradingFee = num(get('Trading Fee'));
  const feeParsed = parseAmountWithAsset(
    get('Network Fee', 'Withdrawal Fee', 'Fee')
  );

  const resolvedFeeAmount =
    isNum(tradingFee)
      ? tradingFee
      : (isNum(feeParsed.amount) ? feeParsed.amount : undefined);

  const resolvedFeeAsset =
    isNum(tradingFee)
      ? asset
      : (feeParsed.asset || asset);

  const isIncome =
    typeRaw.includes('reward') ||
    typeRaw.includes('staking') ||
    typeRaw.includes('earn') ||
    typeRaw.includes('interest') ||
    typeRaw.includes('airdrop') ||
    typeRaw.includes('saving') ||
    typeRaw.includes('savings') ||
    typeRaw.includes('launchpool') ||
    typeRaw.includes('kickstarter') ||
    typeRaw.includes('rebate') ||
    typeRaw.includes('bonus') ||
    typeRaw.includes('referral') ||
    typeRaw.includes('commission');

  const isDeposit =
    typeRaw.includes('deposit') ||
    directionRaw === 'in' ||
    directionRaw === 'deposit';

  const isWithdrawal =
    typeRaw.includes('withdraw') ||
    typeRaw.includes('send') ||
    directionRaw === 'out' ||
    directionRaw === 'withdrawal';

  const isTransfer =
    typeRaw.includes('transfer') ||
    typeRaw.includes('internal transfer');

  const fileSuggestsDeposit =
    fileName.includes('deposit history') || fileName.includes('deposit');

  const fileSuggestsWithdrawal =
    fileName.includes('withdrawal history') ||
    fileName.includes('withdraw history') ||
    fileName.includes('withdrawal');

  if (ts && asset) {
    if (isIncome && isNum(genericAmount)) {
      return mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'income',
        base: asset,
        baseAmount: Math.abs(genericAmount),
        txId: txRef,
        note: `MEXC ${typeRaw || 'income'}`
      });
    }

    if ((fileSuggestsWithdrawal || isWithdrawal) && isNum(resolvedWithdrawalAmount)) {
      return mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'withdrawal',
        base: asset,
        baseAmount: Math.abs(resolvedWithdrawalAmount),
        feeAmount: isNum(resolvedFeeAmount) ? Math.abs(resolvedFeeAmount) : undefined,
        feeAsset: resolvedFeeAsset,
        txId: txRef,
        note:
          `MEXC withdrawal` +
          `${network ? ` (${network})` : ''}` +
          `${status ? ` - ${status}` : ''}` +
          `${address ? ` -> ${address}` : ''}` +
          `${memo && memo !== '--' ? ` memo:${memo}` : ''}` +
          `${progress ? ` progress:${progress}` : ''}` +
          `${isNum(requestAmount) ? ` gross:${requestAmount}` : ''}`
      });
    }

    if ((fileSuggestsDeposit || isDeposit) && isNum(resolvedDepositAmount)) {
      return mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'deposit',
        base: asset,
        baseAmount: Math.abs(resolvedDepositAmount),
        txId: txRef,
        note:
          `MEXC deposit` +
          `${network ? ` (${network})` : ''}` +
          `${status ? ` - ${status}` : ''}` +
          `${progress ? ` ${progress}` : ''}`
      });
    }

    if (isTransfer && isNum(genericAmount)) {
      return mexcTx({
        exchange: 'mexc',
        ctx,
        ts,
        type: 'transfer',
        base: asset,
        baseAmount: Math.abs(genericAmount),
        txId: txRef,
        note: `MEXC transfer${typeRaw ? ` ${typeRaw}` : ''}`
      });
    }
  }

  return null;
}

function parseBitget(row, ctx){
  const ts = toUtc(row['Date'] || row['date'] || row['Time']);
  const pair = (row['Pair'] || row['pair'] || row['Instrument'] || '').toUpperCase();
  const [base, quote] = splitPair(pair);
  const side = String(row['Side'] || row['side'] || '').toLowerCase();
  const price = num(row['Price'] || row['price']);
  const baseAmount = num(row['Filled'] || row['filled'] || row['Amount'] || row['amount']);
  const total = num(row['Total'] || row['total']);
  const fee = num(row['Fee'] || row['fee']);
  const feeAsset = String(row['Fee Asset'] || row['fee coin'] || '').toUpperCase();
  const orderId = row['Order ID'] || row['order id'] || '';
  const instrument = String(row['Instrument'] || '').toLowerCase();
  const isMargin = row['Margin'] || row['margin'] || false;

  // Order History / Trade History Export (bevorzugt)
  if (row['Pair'] && row['Side'] && row['Price']) {
    return normalTx({
      exchange: 'bitget', ctx, ts, type: 'trade', side,
      base, baseAmount,
      quote, quoteAmount: total || round(price * baseAmount), price,
      feeAmount: fee, feeAsset: feeAsset || quote,
      txId: orderId, note: `Bitget ${instrument || 'spot'} ${isMargin ? 'margin' : ''} ${side} ${baseAmount} ${base}`
    });
  }

  // Asset History (Fund Flow) Export
  const assetType = String(row['Type'] || '').toLowerCase();
  const asset = String(row['Asset'] || '').toUpperCase();
  const amount = num(row['Amount'] || row['amount']);
  const detail = row['Detail'] || row['Remark'] || '';
  const orderRef = row['Order ID'] || row['Trade ID'] || '';

  if (asset && amount !== undefined) {
    // Bestimme Transaktionstyp basierend auf Bitget-Type
    let txType = 'deposit';
    if (assetType === 'trade') txType = 'trade';
    else if (assetType === 'fee') txType = 'fee';
    else if (assetType === 'deposit') txType = 'deposit';
    else if (assetType === 'withdraw') txType = 'withdrawal';
    else if (assetType === 'transfer') txType = 'transfer';
    else if (assetType === 'interest') txType = 'income';
    else if (assetType === 'settlement') txType = 'income';
    else if (assetType === 'rebate') txType = 'income';

    return normalTx({
      exchange: 'bitget', ctx, ts, type: txType,
      base: asset, baseAmount: Math.abs(amount),
      feeAmount: assetType === 'fee' ? Math.abs(amount) : undefined,
      feeAsset: assetType === 'fee' ? asset : undefined,
      txId: orderRef, note: `Bitget ${assetType} ${detail}`
    });
  }

  // Account Statement Export (monatliche Zusammenfassung)
  const statementType = String(row['Type'] || '').toLowerCase();
  const statementAsset = String(row['Asset'] || '').toUpperCase();
  const statementAmount = num(row['Amount'] || row['amount']);
  const statementPnL = num(row['PnL'] || row['Realized PnL']);

  if (statementAsset && statementAmount !== undefined && statementType.includes('statement')) {
    return normalTx({
      exchange: 'bitget', ctx, ts, type: 'income',
      base: statementAsset, baseAmount: Math.abs(statementAmount),
      txId: orderRef, note: `Bitget statement ${statementType} PnL: ${statementPnL || 0}`
    });
  }

  return null;
}

function parseOkx(row, ctx){
  const ts = toUtc(row['Date'] || row['Time']);
  const type = String(row['Type'] || '').toLowerCase();
  const currency = String(row['Currency'] || '').toUpperCase();
  const amount = num(row['Amount']);
  const balance = num(row['Balance']);
  const orderId = row['Order ID'] || row['TxID'] || '';
  const subType = row['Sub-type'] || '';
  const account = row['Account'] || '';
  const okxTx = (params) => normalTx({ exchangeAccountId: account, ...params });

  // OKX Ledger-ähnliche Struktur: Trade-Einträge müssen gruppiert werden
  if (type === 'trade' && orderId) {
    // Speichere Trade-Eintrag für spätere Gruppierung
    // Dies wird im postProcess zusammengeführt
    return {
      exchange: 'okx',
      exchangeAccountId: normalizeExchangeAccountId(account),
      exchange_account_id: normalizeExchangeAccountId(account),
      sourceFile: ctx.file,
      row: ctx.rowIndex,
      timestamp: ts,
      type: 'trade_raw', // Markierung für Gruppierung
      currency,
      amount,
      orderId,
      account,
      subType,
      note: `OKX trade raw ${currency} ${amount}`
    };
  }

  // Fee-Einträge (separate Zeilen)
  if (type === 'fee') {
    return okxTx({
      exchange: 'okx', ctx, ts, type: 'fee',
      base: currency, baseAmount: Math.abs(amount),
      feeAmount: Math.abs(amount), feeAsset: currency,
      txId: orderId, note: `OKX fee ${currency}`
    });
  }

  // Deposit/Withdrawal
  if (type === 'deposit') {
    return okxTx({
      exchange: 'okx', ctx, ts, type: 'deposit',
      base: currency, baseAmount: Math.abs(amount),
      txId: orderId, note: `OKX deposit ${account}`
    });
  }

  if (type === 'withdrawal') {
    return okxTx({
      exchange: 'okx', ctx, ts, type: 'withdrawal',
      base: currency, baseAmount: Math.abs(amount),
      txId: orderId, note: `OKX withdrawal ${account}`
    });
  }

  // Transfer (interne Umbuchungen)
  if (type === 'transfer') {
    return okxTx({
      exchange: 'okx', ctx, ts, type: 'transfer',
      base: currency, baseAmount: Math.abs(amount),
      txId: orderId, note: `OKX transfer ${account}`
    });
  }

  // Mining/Staking Rewards
  if (type === 'mining' || type === 'staking' || type === 'reward') {
    return okxTx({
      exchange: 'okx', ctx, ts, type: 'income',
      base: currency, baseAmount: Math.abs(amount),
      txId: orderId, note: `OKX ${type} ${currency}`
    });
  }

  return null;
}

function parseHuobi(row, ctx){
  const ts = toUtc(row['Date'] || row['Time'] || row['Datum'] || row['Zeit'] || row['时间'] || row['日時'], { exchange: 'huobi' });
  const type = normalizeSide(row['Type'] || row['Typ'] || row['类型'] || row['種類'] || '');
  const asset = String(row['Asset'] || row['Währung'] || row['资产'] || row['資産'] || '').toUpperCase();
  const amount = num(row['Amount'] || row['Menge'] || row['Betrag'] || row['数量'] || row['数量']);
  const price = num(row['Price'] || row['Preis'] || row['Kurs'] || row['价格'] || row['価格']);
  const fee = num(row['Fee'] || row['Gebühr'] || row['手续费'] || row['手数料']);
  const transferId = row['Transfer ID'] || row['TxID'] || row['Transfer Id'] || row['转账id'] || row['転送id'] || '';
  const notes = row['Notes'] || row['Notizen'] || row['备注'] || '';
  const inOut = String(row['In/Out'] || row['InOut'] || row['入出'] || '').toLowerCase();

  // Exchange Tab: Trades und interne Umtauschvorgänge
  if (type === 'buy' || type === 'sell' || type === 'exchange trade') {
    // Fall 1: Pro Trade eine Zeile mit separaten In/Out-Spalten
    const amountIn = num(row['Amount In'] || row['Received']);
    const assetIn = String(row['Asset In'] || row['Received Asset'] || '').toUpperCase();
    const amountOut = num(row['Amount Out'] || row['Paid']);
    const assetOut = String(row['Asset Out'] || row['Paid Asset'] || '').toUpperCase();

    if (amountIn && assetIn && amountOut && assetOut) {
      // Bestimme Base/Quote und Side
      const base = type === 'buy' ? assetIn : assetOut;
      const quote = type === 'buy' ? assetOut : assetIn;
      const baseAmount = type === 'buy' ? amountIn : amountOut;
      const quoteAmount = type === 'buy' ? amountOut : amountIn;
      const side = type === 'buy' ? 'buy' : 'sell';
      const tradePrice = price || (quoteAmount / baseAmount);

      return normalTx({
        exchange: 'huobi', ctx, ts, type: 'trade', side,
        base, baseAmount,
        quote, quoteAmount, price: tradePrice,
        feeAmount: fee, feeAsset: fee ? assetOut : undefined,
        txId: transferId, note: `Huobi ${type} ${notes}`
      });
    }

    // Fall 2: Pro Trade zwei Zeilen (ähnlich OKX)
    if (type === 'exchange trade' && transferId) {
      return {
        exchange: 'huobi',
        exchangeAccountId: 'default',
        exchange_account_id: 'default',
        sourceFile: ctx.file,
        row: ctx.rowIndex,
        timestamp: ts,
        type: 'trade_raw', // Markierung für Gruppierung
        asset,
        amount,
        transferId,
        inOut,
        note: `Huobi trade raw ${asset} ${amount} ${inOut}`
      };
    }
  }

  // Transfer Tab: Einzahlungen/Auszahlungen
  if (type === 'deposit' || type === 'withdrawal') {
    const txType = type === 'deposit' ? 'deposit' : 'withdrawal';
    const netAmount = amount - (fee || 0); // Netto-Betrag nach Gebühr

    return normalTx({
      exchange: 'huobi', ctx, ts, type: txType,
      base: asset, baseAmount: Math.abs(netAmount),
      feeAmount: fee, feeAsset: asset,
      txId: transferId, note: `Huobi ${type} ${notes}`
    });
  }

  // Rewards/Airdrops/Bonus
  if (type === 'crypto bonus' || type === 'fork airdrop' || type === 'reward' || type === 'interest') {
    return normalTx({
      exchange: 'huobi', ctx, ts, type: 'income',
      base: asset, baseAmount: Math.abs(amount),
      txId: transferId, note: `Huobi ${type} ${notes}`
    });
  }

  // Interne Transfers (Main ↔ Futures)
  if (type === 'transfer' || type === 'internal transfer') {
    return normalTx({
      exchange: 'huobi', ctx, ts, type: 'transfer',
      base: asset, baseAmount: Math.abs(amount),
      txId: transferId, note: `Huobi transfer ${notes}`
    });
  }

  // Margin/Futures-spezifische Typen
  if (type === 'margin trade' || type === 'futures trade') {
    const side = inOut === 'in' ? 'buy' : 'sell';
    return normalTx({
      exchange: 'huobi', ctx, ts, type: 'trade', side,
      base: asset, baseAmount: Math.abs(amount),
      price: price,
      txId: transferId, note: `Huobi ${type} ${side}`
    });
  }

  return null;
}

function parseCryptoCom(row, ctx){
  // ===============================
  // ✅ Crypto.com App/Web Export: "Transaktionsverlauf"
  // Headers: Timestamp (UTC), Transaction Kind, Currency, Amount, To Currency, To Amount, Transaction Hash
  // ===============================
  if (row['Timestamp (UTC)'] !== undefined || row['Transaction Kind'] !== undefined) {
    const ts = toUtc(row['Timestamp (UTC)'] || row['Timestamp'] || row['Date']);
    const kind = String(row['Transaction Kind'] || '').trim().toLowerCase();
    const desc = String(row['Transaction Description'] || row['Description'] || '').trim();

    const cur = String(row['Currency'] || '').trim().toUpperCase();
    const amt = num(row['Amount']);

    const toCur = String(row['To Currency'] || '').trim().toUpperCase();
    const toAmt = num(row['To Amount']);

    const txHash = row['Transaction Hash'] || row['Hash'] || row['TxID'] || row['Transaction ID'] || '';

    // ---- Deposits / Withdrawals ----
    if (kind === 'crypto_deposit') {
      if (!ts || !cur || !isNum(amt)) return null;
      return normalTx({
        exchange: 'crypto_com', ctx, ts, type: 'deposit',
        base: cur, baseAmount: Math.abs(amt),
        txId: txHash,
        note: `Crypto.com deposit ${cur}`
      });
    }

    if (kind === 'crypto_withdrawal') {
      if (!ts || !cur || !isNum(amt)) return null;
      return normalTx({
        exchange: 'crypto_com', ctx, ts, type: 'withdrawal',
        base: cur, baseAmount: Math.abs(amt),
        txId: txHash,
        note: `Crypto.com withdrawal ${cur}`
      });
    }

    // ---- Swaps/Conversions ----
    // crypto_exchange: Currency/Amount = Abfluss oder Zufluss, To Currency/To Amount = Gegenasset
    if (kind === 'crypto_exchange' && cur && isNum(amt) && toCur && isNum(toAmt)) {
      const side = (amt < 0) ? 'sell' : 'buy';
      const base = cur;
      const quote = toCur;
      const baseAmount = Math.abs(amt);
      const quoteAmount = Math.abs(toAmt);
      const price = baseAmount ? round(quoteAmount / baseAmount) : undefined;

      return normalTx({
        exchange: 'crypto_com', ctx, ts, type: 'trade', side,
        base, baseAmount,
        quote, quoteAmount,
        price,
        txId: txHash,
        note: `Crypto.com swap ${base} ${side} -> ${quote}`
      });
    }

    // ---- Fiat Buy/Sell Bridges (falls vorhanden) ----
    // viban_purchase: Fiat -> Crypto (BUY)
    if (kind === 'viban_purchase' && cur && isNum(amt) && toCur && isNum(toAmt)) {
      const base = toCur;
      const baseAmount = Math.abs(toAmt);
      const quote = cur;
      const quoteAmount = Math.abs(amt);
      return normalTx({
        exchange: 'crypto_com', ctx, ts, type: 'trade', side: 'buy',
        base, baseAmount,
        quote, quoteAmount,
        price: baseAmount ? round(quoteAmount / baseAmount) : undefined,
        txId: txHash,
        note: `Crypto.com viban purchase ${base}`
      });
    }

    // crypto_viban(_exchange): Crypto -> Fiat (SELL)
    if ((kind === 'crypto_viban' || kind === 'crypto_viban_exchange') && cur && isNum(amt) && toCur && isNum(toAmt)) {
      const base = cur;
      const baseAmount = Math.abs(amt);
      const quote = toCur;
      const quoteAmount = Math.abs(toAmt);
      return normalTx({
        exchange: 'crypto_com', ctx, ts, type: 'trade', side: 'sell',
        base, baseAmount,
        quote, quoteAmount,
        price: baseAmount ? round(quoteAmount / baseAmount) : undefined,
        txId: txHash,
        note: `Crypto.com viban sell ${base}`
      });
    }

    // Wenn es Transaktionsverlauf ist, aber unbekannter Kind: ignoriere
    return null;
  }

  // ===============================
  // 🔽 Ab hier: dein EXISTIERENDER Code unverändert
  // ===============================

  // --- Existing Crypto.com Exchange + legacy App parsers ---
  const ts = toUtc(row['Date'] || row['Timestamp']);
  const description = String(row['Description'] || row['Type'] || '').toLowerCase();
  const asset = String(row['Asset'] || row['Currency'] || row['Market'] || '').toUpperCase();
  const amount = num(row['Amount'] || row['Betrag'] || row['Quantity']);
  const value = num(row['Value'] || row['Gegenwert'] || row['USD Value'] || row['Total']);
  const currency = String(row['Currency'] || row['Währung'] || 'USD').toUpperCase();
  const fee = num(row['Fee']);
  const feeAsset = String(row['Fee Currency'] || '').toUpperCase();
  const instrument = String(row['Instrument'] || '').toLowerCase();
  const remark = row['Remark'] || row['Order ID'] || '';

  // Crypto.com Exchange: Trade History CSV
  const pair = row['Market'] || row['Pair'] || '';
  const side = String(row['Side'] || '').toLowerCase();
  const price = num(row['Price']);
  const baseAmount = num(row['Quantity'] || row['Amount']);
  const total = num(row['Total']);

  if (pair && side && price && baseAmount) {
    const [base, quote] = splitPair(pair);
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'trade', side,
      base, baseAmount,
      quote, quoteAmount: total || round(price * baseAmount), price,
      feeAmount: fee, feeAsset: feeAsset || quote,
      txId: remark, note: `Crypto.com exchange ${instrument || 'spot'} ${side} ${base}`
    });
  }

  // Crypto.com Exchange: Wallet Transactions History (Ledger-ähnlich)
  if (description.includes('trade spot') || description.includes('trade futures')) {
    // Speichere Trade-Eintrag für spätere Gruppierung (ähnlich OKX)
    return {
      exchange: 'crypto_com',
      exchangeAccountId: 'default',
      exchange_account_id: 'default',
      sourceFile: ctx.file,
      row: ctx.rowIndex,
      timestamp: ts,
      type: 'trade_raw', // Markierung für Gruppierung
      asset,
      amount,
      remark,
      instrument,
      note: `Crypto.com exchange trade raw ${asset} ${amount}`
    };
  }

  // Crypto.com Exchange: Deposits & Withdrawals CSV
  const txId = row['TxID'] || row['Transaction ID'] || row['Hash'] || '';
  const network = row['Network'] || '';
  const status = row['Status'] || '';

  if (description.includes('deposit') || description.includes('withdrawal')) {
    const txType = description.includes('deposit') ? 'deposit' : 'withdrawal';
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: txType,
      base: asset, baseAmount: Math.abs(amount),
      feeAmount: fee, feeAsset: feeAsset || asset,
      txId, note: `Crypto.com exchange ${txType} ${network} ${status}`
    });
  }

  // Crypto.com Exchange: Rewards CSV (Lockup, Referral)
  if (description.includes('lockup') || description.includes('referral') || description.includes('supercharger')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'income',
      base: asset, baseAmount: Math.abs(amount),
      txId, note: `Crypto.com exchange ${description} ${asset}`
    });
  }

  // Crypto.com App: Crypto Wallet CSV
  if (description.includes('buy') || description.includes('bought')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'trade', side: 'buy',
      base: asset, baseAmount: Math.abs(amount),
      quote: currency, quoteAmount: Math.abs(value),
      feeAmount: fee, feeAsset: feeAsset || currency,
      note: `Crypto.com app buy ${asset}`
    });
  }

  if (description.includes('sell') || description.includes('sold')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'trade', side: 'sell',
      base: asset, baseAmount: Math.abs(amount),
      quote: currency, quoteAmount: Math.abs(value),
      feeAmount: fee, feeAsset: feeAsset || currency,
      note: `Crypto.com app sell ${asset}`
    });
  }

  // Staking Rewards / Supercharger
  if (description.includes('supercharger') || description.includes('staking') || description.includes('reward')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'income',
      base: asset, baseAmount: Math.abs(amount),
      note: `Crypto.com app ${description} ${asset}`
    });
  }

  // Earn Interest
  if (description.includes('earn') || description.includes('interest')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'income',
      base: asset, baseAmount: Math.abs(amount),
      note: `Crypto.com app earn ${asset}`
    });
  }

  // Referral Bonus / Promotions
  if (description.includes('bonus') || description.includes('referral') || description.includes('promotion')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'income',
      base: asset, baseAmount: Math.abs(amount),
      note: `Crypto.com app bonus ${asset}`
    });
  }

  // Visa Card Top-up (Krypto → Fiat)
  if (description.includes('visa card top-up') || description.includes('card top-up')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'trade', side: 'sell',
      base: asset, baseAmount: Math.abs(amount),
      quote: currency, quoteAmount: Math.abs(value),
      note: `Crypto.com app visa top-up ${asset}`
    });
  }

  // Send/Receive (Transfers)
  if (description.includes('send') || description.includes('sent')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'withdrawal',
      base: asset, baseAmount: Math.abs(amount),
      feeAmount: fee, feeAsset: feeAsset || asset,
      note: `Crypto.com app send ${asset}`
    });
  }

  if (description.includes('receive') || description.includes('received')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'deposit',
      base: asset, baseAmount: Math.abs(amount),
      note: `Crypto.com app receive ${asset}`
    });
  }

  // Cash Wallet CSV: Fiat-Transaktionen
  if (description.includes('top-up') || description.includes('sepa')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'deposit',
      base: asset, baseAmount: Math.abs(amount),
      note: `Crypto.com app fiat top-up ${asset}`
    });
  }

  if (description.includes('withdrawal') || description.includes('withdraw')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'withdrawal',
      base: asset, baseAmount: Math.abs(amount),
      feeAmount: fee, feeAsset: feeAsset || asset,
      note: `Crypto.com app fiat withdrawal ${asset}`
    });
  }

  if (description.includes('spent on') || description.includes('purchase')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'withdrawal',
      base: asset, baseAmount: Math.abs(amount),
      note: `Crypto.com app spent ${asset}`
    });
  }

  // Visa Card CSV: Karten-Transaktionen
  if (description.includes('cashback') || description.includes('rebate')) {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'income',
      base: asset, baseAmount: Math.abs(amount),
      note: `Crypto.com app cashback ${asset}`
    });
  }

  // Card Top-up (Fiat-Seite)
  if (description.includes('card top-up') && asset !== 'CRO') {
    return normalTx({
      exchange: 'crypto_com', ctx, ts, type: 'deposit',
      base: asset, baseAmount: Math.abs(amount),
      note: `Crypto.com app card top-up fiat ${asset}`
    });
  }

  // Card Spending (normal, nicht steuerrelevant)
  if (description.includes('spent') && !description.includes('on')) {
    return null; // Normale Kartenausgaben sind nicht steuerrelevant
  }

  return null;
}

function parseUpbit(row, ctx){
  const ts = toUtc(row['날짜/시간'] || row['Date'] || row['Time'], { exchange: 'upbit' });
  const side = String(row['구분'] || row['Side'] || '').toLowerCase();
  const asset = String(row['자산'] || row['Asset'] || '').toUpperCase();
  const amount = num(row['주문량'] || row['Amount']);
  const price = num(row['체결가'] || row['Price']);
  const total = num(row['체결금액'] || row['Total']);
  const fee = num(row['수수료'] || row['Fee']);
  
  // Koreanische Side-Normalisierung
  let normalizedSide = side;
  if (side === '매수' || side === 'buy') normalizedSide = 'buy';
  else if (side === '매도' || side === 'sell') normalizedSide = 'sell';
  
  // KRW-Pair-Erkennung (Upbit-spezifisch)
  let base, quote;
  if (asset.includes('KRW')) {
    // KRW-BTC → BTC/KRW
    base = asset.replace('KRW-', '').replace('KRW_', '').replace('KRW', '');
    quote = 'KRW';
  } else {
    // Crypto-Crypto Trades (falls unterstützt)
    [base, quote] = splitPair(asset);
  }
  
  const upbitKindRaw = String(
    row['구분'] || row['Type'] || row['Category'] || row['Side'] || ''
  ).toLowerCase().trim();

  const upbitIncome =
    upbitKindRaw.includes('reward') ||
    upbitKindRaw.includes('staking') ||
    upbitKindRaw.includes('earn') ||
    upbitKindRaw.includes('interest') ||
    upbitKindRaw.includes('airdrop');

  if (asset && isNum(amount) && upbitIncome && !price) {
    return normalTx({
      exchange: 'upbit', ctx, ts, type: 'income',
      base: asset, baseAmount: Math.abs(amount),
      note: `Upbit ${upbitKindRaw || 'income'}`
    });
  }

  if (!ts || !base || !quote || !normalizedSide || !price || !amount) return null;
  
  return normalTx({
    exchange: 'upbit', ctx, ts, type: 'trade', side: normalizedSide,
    base, baseAmount: amount,
    quote, quoteAmount: total || round(price * amount), price,
    feeAmount: fee, feeAsset: quote,
    note: `Upbit ${normalizedSide} ${amount} ${base}`
  });
}

function parseBitvavo(row, ctx){
  // Zeit: Bitvavo liefert Date + Time ohne Zeitzone (Wanduhr Europe/Amsterdam)
  const tsParsed = parseExchangeInstant(
    `${row['Date'] || ''} ${row['Time'] || ''}`.trim(),
    { exchange: 'bitvavo' }
  );
  const ts = tsParsed.iso;
  ctx = { ...ctx, exchangeTimeInterpretation: tsParsed.interpretation };

  const typeRaw = String(row['Type'] || '').toLowerCase().trim();
  const currency = (row['Currency'] || '').toUpperCase();
  const amount = num(row['Amount']);

  const rpCurrency = (row['Received / Paid Currency'] || row['Received/Paid Currency'] || '').toUpperCase();
  const rpAmount  = num(row['Received / Paid Amount'] || row['Received/Paid Amount']);

  const quoteCur  = (row['Quote Currency'] || '').toUpperCase();
  const quotePrice = num(row['Quote Price']);

  const feeAmount = num(row['Fee amount'] || row['Fee Amount'] || row['Fee']);
  const feeAsset  = (row['Fee currency'] || row['Fee Currency'] || '').toUpperCase();

  const txId = row['Transaction ID'] || row['TxID'] || '';
  const address = row['Address'] || '';

  // ---- Trades (buy/sell) ----
  if (typeRaw === 'buy' || typeRaw === 'sell') {
    const side = typeRaw;                     // 'buy' | 'sell'
    const base = currency;                    // z. B. VET
    const baseAmount = Math.abs(amount);      // immer positiv
    const quote = rpCurrency || quoteCur || ''; // EUR o. ä.
    const quoteAmount = (typeof rpAmount === 'number')
      ? Math.abs(rpAmount)
      : (typeof quotePrice === 'number' && typeof baseAmount === 'number'
          ? Math.round(quotePrice * baseAmount * 1e12) / 1e12
          : undefined);

    return normalTx({
      exchange: 'bitvavo', ctx, ts, type: 'trade', side,
      base, baseAmount,
      quote, quoteAmount,
      price: quotePrice,
      feeAmount, feeAsset,
      txId,
      note: `Bitvavo ${side} ${baseAmount} ${base}`
    });
  }

  // ---- Ein-/Auszahlungen ----
  if (typeRaw === 'deposit') {
    return normalTx({
      exchange: 'bitvavo', ctx, ts, type: 'deposit',
      base: currency, baseAmount: Math.abs(amount),
      feeAmount, feeAsset: feeAsset || currency, // Bitvavo nimmt oft 0 bei Deposits
      txId, note: address ? `Bitvavo deposit → ${address}` : 'Bitvavo deposit'
    });
  }

  if (typeRaw === 'withdrawal' || typeRaw === 'withdraw') {
    return normalTx({
      exchange: 'bitvavo', ctx, ts, type: 'withdrawal',
      base: currency, baseAmount: Math.abs(amount),
      feeAmount, feeAsset: feeAsset || currency,
      txId, note: address ? `Bitvavo withdrawal to ${address}` : 'Bitvavo withdrawal'
    });
  }

  // ---- Rewards / Staking / Interest / Airdrop ----
  if (
    typeRaw.includes('staking') ||
    typeRaw.includes('reward') ||
    typeRaw.includes('interest') ||
    typeRaw.includes('airdrop') ||
    typeRaw.includes('bonus') ||
    typeRaw.includes('affiliate')
  ) {
    return normalTx({
      exchange: 'bitvavo', ctx, ts, type: 'income',
      base: currency, baseAmount: Math.abs(amount),
      feeAmount, feeAsset: feeAsset || currency,
      txId,
      note: `Bitvavo ${typeRaw || 'income'}`
    });
  }

  // Sonst ignorieren
  return null;
}

function readCell(row, keys = []) {
  if (!row || typeof row !== 'object') return undefined;
  const lowerKeyToVal = {};
  for (const k of Object.keys(row)) {
    lowerKeyToVal[String(k).toLowerCase().trim()] = row[k];
  }
  for (const key of keys) {
    const lk = String(key).toLowerCase().trim();
    const value = lowerKeyToVal[lk] ?? row[key];
    if (value != null && String(value).trim() !== '') {
      return value;
    }
  }
  return undefined;
}

function makeImportIssue({
  file = null,
  row = null,
  column = null,
  code = 'IMPORT_VALIDATION_ERROR',
  message = 'Import validation failed',
  severity = 'error',
  template = false,
  ...rest
} = {}) {
  return {
    file,
    row,
    column,
    code,
    message,
    severity,
    template,
    ...rest,
  };
}

function formatImportIssue(issue) {
  const parts = [];
  if (issue?.file) parts.push(issue.file);
  if (Number.isInteger(issue?.row)) parts.push(`row ${issue.row}`);
  if (issue?.column) parts.push(issue.column);
  const prefix = parts.length ? `${parts.join(' • ')}: ` : '';
  return `${prefix}${issue?.message || 'Import validation failed'}`;
}

function throwImportIssue(details = {}) {
  const issue = makeImportIssue(details);
  const err = new Error(issue.message);
  err.importIssue = issue;
  throw err;
}

/** V3Tax-Standard-Template: EN/DE-Header + ältere Export-Varianten */
const V3_STANDARD_HEADER_ALIASES = {
  source: [
    'source',
    'source (optional)',
    'exchange',
    'platform',
    'quelle',
    'quelle (optional)',
    'exchange / source',
    'exchange / source (optional)',
    'börse / quelle',
    'boerse / quelle',
    'börse',
    'boerse',
    'börse / quelle (optional)',
    'boerse / quelle (optional)',
    'quelle / börse',
    'quelle / börse (optional)',
    'quelle / boerse',
    'quelle / boerse (optional)',
  ],
  kind: [
    'kind',
    'type',
    'transaction_type',
    'art',
    'typ',
    'vorgang',
  ],
  timestamp_utc: [
    'timestamp_utc',
    'timestamp utc',
    'timestamp',
    'timestamp (utc)',
    'zeitstempel utc',
    'zeitstempel',
    'zeitpunkt (utc)',
    'zeitpunkt utc',
    'zeitpunkt',
  ],
  base: [
    'base',
    'asset',
    'token',
  ],
  amount: [
    'amount',
    'base_amount',
    'quantity',
    'qty',
    'menge',
  ],
  side: [
    'side',
    'buy / sell',
    'buy/sell',
    'kauf / verkauf',
    'kauf/verkauf',
    'richtung',
  ],
  quote: [
    'quote',
    'quote_asset',
    'gegenwert',
    'gegenwert-währung',
    'gegenwert-waehrung',
    'quote asset',
    'price asset',
    'preis-asset',
    'preis asset',
    'preiswährung',
    'preiswaehrung',
    'gegenwährung',
    'gegenwaehrung',
  ],
  quote_amount: [
    'quote_amount',
    'quote amount',
    'quote_total',
    'total',
    'gegenwert-betrag',
    'gegenwert betrag',
    'total value',
    'gesamtwert',
    'gegenwert (betrag)',
  ],
  price: [
    'price',
    'preis',
    'price (optional)',
    'preis (optional)',
    'unit price',
    'stückpreis',
    'stueckpreis',
    'stückpreis (optional)',
    'stueckpreis (optional)',
  ],
  fee: [
    'fee',
    'fee_amount',
    'gebühr',
    'gebuehr',
    'gebühr (optional)',
    'gebuehr (optional)',
    // Varianten mit Leerzeichen / englisches Label aus dem Standard-Template
    'fee amount',
    'fee amount (optional)',
    'fee (optional)',
  ],
  fee_asset: [
    'fee_asset',
    'fee asset',
    'fee_currency',
    'gebühren-asset',
    'gebuehren-asset',
    'gebühren-asset (optional)',
    'gebuehren-asset (optional)',
    // Varianten ohne Bindestrich (ältere / abgewandelte Templates)
    'gebühren asset',
    'gebuehren asset',
    'gebühren asset (optional)',
    'gebuehren asset (optional)',
    'fee asset (optional)',
  ],
  txid: [
    'txid',
    'txid (optional)',
    'tx_id',
    'transaction_id',
    'order_id',
    'txid / referenz',
    'txid / reference',
    'referenz',
    'reference',
  ],
  notes: [
    'notes',
    'notes (optional)',
    'note',
    'comment',
    'notiz',
    'notiz (optional)',
    'notizen',
    'notizen (optional)',
    'kommentar',
  ],
  account_id: [
    'account_id',
    'account id',
    'account id (optional)',
    'konto-id',
    'konto-id (optional)',
    'konto id',
  ],
};

function headerMatchesAny(header, aliases = []) {
  const normalizedHeader = String(header || '').trim().toLowerCase();
  return aliases.some((alias) => normalizedHeader === String(alias || '').trim().toLowerCase());
}

function looksLikeV3TaxStandardAttempt(headers = []) {
  const normalized = (headers || []).map((h) =>
    String(h || '').trim().toLowerCase()
  );

  const canonicalKeys = Object.keys(V3_STANDARD_HEADER_ALIASES);
  const requiredKeys = ['source', 'kind', 'timestamp_utc', 'base', 'amount'];

  const matches = canonicalKeys.filter((key) =>
    normalized.some((header) => headerMatchesAny(header, V3_STANDARD_HEADER_ALIASES[key]))
  );

  const missingRequired = requiredKeys.filter((key) =>
    !normalized.some((header) => headerMatchesAny(header, V3_STANDARD_HEADER_ALIASES[key]))
  );

  return {
    isLikely: matches.length >= 3,
    matches,
    missingRequired,
  };
}

function parseTemplateKind(rawKind, ctx = {}) {
  const original = String(rawKind || '').trim();

  if (!original) {
    throwImportIssue({
      file: ctx?.file,
      row: ctx?.rowIndex,
      column: 'kind',
      code: 'MISSING_KIND',
      message: 'The field "kind" is required.',
      template: true,
    });
  }

  const normKind = original
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');

  if (normKind === 'buy' || normKind === 'kauf') return { type: 'trade', impliedSide: 'buy' };
  if (normKind === 'sell' || normKind === 'verkauf') return { type: 'trade', impliedSide: 'sell' };

  if (['trade', 'spot', 'swap', 'handel'].includes(normKind)) {
    return { type: 'trade', impliedSide: null };
  }

  if (['deposit', 'transfer_in', 'transferin', 'einzahlung'].includes(normKind)) {
    return { type: 'deposit', impliedSide: null };
  }

  if (['withdrawal', 'withdraw', 'transfer_out', 'transferout', 'auszahlung'].includes(normKind)) {
    return { type: 'withdrawal', impliedSide: null };
  }

  if (
    [
      'income',
      'other_income',
      'otherincome',
      'reward',
      'rewards',
      'staking',
      'staking_reward',
      'interest',
      'airdrop',
      'einnahme',
      'einnahmen',
      'belohnung',
      'belohnungen',
      'ertrag',
      'erträge',
      'ertraege'
    ].includes(normKind)
  ) {
    return { type: 'income', impliedSide: null };
  }

  throwImportIssue({
    file: ctx?.file,
    row: ctx?.rowIndex,
    column: 'kind',
    code: 'UNSUPPORTED_KIND',
    message:
      `Unsupported kind "${original}". Allowed values: trade, buy, sell, deposit, withdrawal, income.`,
    template: true,
  });
}

function parseV3TaxStandardRow(row, ctx) {
  const hasValue = (v) => v != null && String(v).trim() !== '';

  const sourceRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.source);
  const kindRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.kind);
  const tsRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.timestamp_utc);
  const baseRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.base);
  const amountRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.amount);
  const sideRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.side);
  const quoteRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.quote);
  const quoteAmountRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.quote_amount);
  const priceRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.price);
  const feeRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.fee);
  const feeAssetRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.fee_asset);
  const txIdRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.txid);
  const notesRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.notes);
  const accountIdRaw = readCell(row, V3_STANDARD_HEADER_ALIASES.account_id);

  const { type, impliedSide } = parseTemplateKind(kindRaw, ctx);
  const ts = toUtc(tsRaw);

  if (!ts) {
    throwImportIssue({
      file: ctx?.file,
      row: ctx?.rowIndex,
      column: 'timestamp_utc',
      code: 'INVALID_TIMESTAMP_UTC',
      message:
        'Invalid "timestamp_utc". Please use UTC in the format YYYY-MM-DD HH:MM:SS.',
      template: true,
    });
  }

  const base = normalizeSymbol(baseRaw);
  const baseAmount = num(amountRaw);

  if (!base) {
    throwImportIssue({
      file: ctx?.file,
      row: ctx?.rowIndex,
      column: 'base',
      code: 'MISSING_BASE',
      message: 'The field "base" is required.',
      template: true,
    });
  }

  if (!isNum(baseAmount) || Number(baseAmount) <= 0) {
    throwImportIssue({
      file: ctx?.file,
      row: ctx?.rowIndex,
      column: 'amount',
      code: 'INVALID_AMOUNT',
      message: 'The field "amount" must be a number greater than 0.',
      template: true,
    });
  }

  const normalizedTemplateSide = (() => {
    const s = String(sideRaw || '').trim().toLowerCase();
    if (!s) return null;
    if (['buy', 'kauf'].includes(s)) return 'buy';
    if (['sell', 'verkauf'].includes(s)) return 'sell';
    return normalizeSide(sideRaw);
  })();

  let side = impliedSide || normalizedTemplateSide;
  let quote = normalizeSymbol(quoteRaw);
  let quoteAmount = num(quoteAmountRaw);
  let price = num(priceRaw);
  const feeAmount = num(feeRaw);
  let feeAsset = normalizeSymbol(feeAssetRaw);

  if (hasValue(quoteAmountRaw) && (!isNum(quoteAmount) || Number(quoteAmount) <= 0)) {
    throwImportIssue({
      file: ctx?.file,
      row: ctx?.rowIndex,
      column: 'quote_amount',
      code: 'INVALID_QUOTE_AMOUNT',
      message: 'If "quote_amount" is filled, it must be a number greater than 0.',
      template: true,
    });
  }

  if (hasValue(priceRaw) && (!isNum(price) || Number(price) <= 0)) {
    throwImportIssue({
      file: ctx?.file,
      row: ctx?.rowIndex,
      column: 'price',
      code: 'INVALID_PRICE',
      message: 'If "price" is filled, it must be a number greater than 0.',
      template: true,
    });
  }

  if (hasValue(feeRaw) && (!isNum(feeAmount) || Number(feeAmount) < 0)) {
    throwImportIssue({
      file: ctx?.file,
      row: ctx?.rowIndex,
      column: 'fee',
      code: 'INVALID_FEE',
      message: 'If "fee" is filled, it must be a valid number 0 or greater.',
      template: true,
    });
  }

  if (type === 'trade') {
    if (side !== 'buy' && side !== 'sell') {
      throwImportIssue({
        file: ctx?.file,
        row: ctx?.rowIndex,
        column: 'side',
        code: 'MISSING_SIDE',
        message:
          'Trade rows require "side" = BUY or SELL. Alternatively use kind=buy or kind=sell.',
        template: true,
      });
    }

    if (!quote) {
      throwImportIssue({
        file: ctx?.file,
        row: ctx?.rowIndex,
        column: 'quote',
        code: 'MISSING_QUOTE',
        message: 'Trade rows require "quote".',
        template: true,
      });
    }

    if (!isNum(quoteAmount) && !isNum(price)) {
      throwImportIssue({
        file: ctx?.file,
        row: ctx?.rowIndex,
        column: 'quote_amount',
        code: 'MISSING_QUOTE_AMOUNT_OR_PRICE',
        message: 'Trade rows require "quote_amount" or "price".',
        template: true,
      });
    }

    if (!isNum(quoteAmount) && isNum(price)) {
      quoteAmount = Math.round(Number(baseAmount) * Number(price) * 1e12) / 1e12;
    }

    if (!isNum(price) && isNum(quoteAmount) && Number(baseAmount) > 0) {
      price = Number(quoteAmount) / Number(baseAmount);
    }

    if (isNum(feeAmount) && !feeAsset) {
      feeAsset = quote;
    }
  }

  if (type === 'income') {
    if (isNum(price) && !quote) {
      throwImportIssue({
        file: ctx?.file,
        row: ctx?.rowIndex,
        column: 'quote',
        code: 'INCOME_PRICE_REQUIRES_QUOTE',
        message: 'Income rows with "price" also require "quote".',
        template: true,
      });
    }

    if (isNum(feeAmount) && !feeAsset) {
      feeAsset = base;
    }
  }

  if ((type === 'deposit' || type === 'withdrawal') && isNum(feeAmount) && !feeAsset) {
    feeAsset = base;
  }

  return {
    ...normalTx({
      exchange: String(sourceRaw || 'manual').trim().toLowerCase(),
      exchangeAccountId: accountIdRaw,
      ctx,
      ts,
      type,
      side,
      base,
      baseAmount: Math.abs(Number(baseAmount)),
      quote,
      quoteAmount: isNum(quoteAmount) ? Math.abs(Number(quoteAmount)) : undefined,
      price: isNum(price) ? Number(price) : undefined,
      feeAmount: isNum(feeAmount) ? Math.abs(Number(feeAmount)) : undefined,
      feeAsset: feeAsset || undefined,
      txId: txIdRaw ? String(txIdRaw).trim() : undefined,
      note: notesRaw ? String(notesRaw).trim() : `V3Tax standard template ${type}`,
    }),
    importSource: 'v3tax_standard',
  };
}

// sehr defensiver Fallback für handelsübliche Spot-CSV
function parseGenericSpotRow(row, ctx){
  const ts = toUtc(row['Timestamp'] || row['Date'] || row['Time']);
  const pair = (row['Pair'] || row['Market'] || row['Symbol'] || `${row['Base']}/${row['Quote']}` || '').toUpperCase();
  const [base, quote] = splitPair(pair);
  const side = String(row['Side'] || row['Type'] || '').toLowerCase();
  const price = num(row['Price']);
  const baseAmount = num(row['Amount'] || row['Qty'] || row['Quantity']);
  const total = num(row['Total']);
  const feeStr = parseFee(row['Fee'], row['Fee Coin']);
  return normalTx({
    exchange:'unknown', ctx, ts, type: side ? 'trade' : 'deposit',
    side, base, baseAmount, quote, quoteAmount: total || round(price*baseAmount), price,
    feeAmount: feeStr.amount, feeAsset: feeStr.asset
  });
}

// ---------- Helpers ----------
export function normalizeExchangeAccountId(value) {
  return normalizeExchangeAccountIdShared(value);
}

export {
  buildExchangeRowFingerprint,
  buildExchangeImportUid,
  buildExchangeAccountScopeKey,
};

function normalTx({
  exchange,
  exchangeAccountId,
  ctx,
  ts,
  type,
  side,
  base,
  baseAmount,
  quote,
  quoteAmount,
  price,
  feeAmount,
  feeAsset,
  txId,
  note,
  exchangeTimeInterpretation,
  importSource,
  sourceAccountId,
  targetAccountId,
}) {
  const normalizedAccountId =
    normalizeExchangeAccountId(exchangeAccountId);

  const baseRow = {
    exchange,
    exchangeAccountId: normalizedAccountId,
    exchange_account_id: normalizedAccountId,
    sourceFile: ctx?.file,
    row: ctx?.rowIndex,
    timestamp: ts,
    type,
    side,
    baseAsset: base,
    baseAmount: isNum(baseAmount) ? baseAmount : undefined,
    quoteAsset: quote,
    quoteAmount: isNum(quoteAmount) ? quoteAmount : undefined,
    price: isNum(price) ? price : undefined,
    feeAsset: feeAsset || undefined,
    feeAmount: isNum(feeAmount) ? feeAmount : undefined,
    txId: txId || undefined,
    note: note || undefined,
    importSource: importSource || undefined,
    sourceAccountId: sourceAccountId
      ? normalizeExchangeAccountId(sourceAccountId)
      : undefined,
    source_account_id: sourceAccountId
      ? normalizeExchangeAccountId(sourceAccountId)
      : undefined,
    targetAccountId: targetAccountId
      ? normalizeExchangeAccountId(targetAccountId)
      : undefined,
    target_account_id: targetAccountId
      ? normalizeExchangeAccountId(targetAccountId)
      : undefined,
    exchangeTimeInterpretation:
      exchangeTimeInterpretation ||
      ctx?.exchangeTimeInterpretation ||
      undefined,
  };

  return ensureImportUid(baseRow);
}

// Utility: robustes Datums-Parsing -> epoch seconds (UTC)
// ---- Time & number helpers ----
function toEpochUtc(input) {
  if (input == null || input === '') return undefined;
  if (typeof input === 'number') {
    return input > 1e12 ? Math.round(input / 1000) : Math.round(input);
  }

  const s = String(input).trim();
  if (!s) return undefined;

  // YYYY-MM-DD HH:mm:ss / YYYY-MM-DDTHH:mm:ss / optional UTC/GMT/Z
  const isoLike = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?\s*(?:UTC|GMT|Z)?$/i
  );
  if (isoLike) {
    const [, y, mo, d, h, mi, se = '00'] = isoLike;
    return Math.floor(Date.UTC(+y, +mo - 1, +d, +h, +mi, +se) / 1000);
  }

  // DE-Format
  const de = s.match(/^(\d{2})\.(\d{2})\.(\d{4}),?\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (de) {
    const [, d, mo, y, H, M, S] = de;
    return Math.floor(Date.UTC(+y, +mo - 1, +d, +H, +M, +S) / 1000);
  }

  const ms = new Date(s).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
}

// 🚀 FIX: Hard guardrails for timestamp validation
function safeIsoFromEpochSeconds(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const d = new Date(n * 1000);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

function validateTimestamp(timestamp, fallbackTimestamp = null, source = 'unknown') {
  const EPOCH_CUTOFF = 31536000;

  const tsNum = Number(timestamp);
  if (Number.isFinite(tsNum) && tsNum > EPOCH_CUTOFF) {
    return Math.floor(tsNum);
  }

  const badIso = safeIsoFromEpochSeconds(tsNum);
  
  const fallbackNum = Number(fallbackTimestamp);
  if (Number.isFinite(fallbackNum) && fallbackNum > EPOCH_CUTOFF) {
    const fallbackIso = safeIsoFromEpochSeconds(fallbackNum);
        return Math.floor(fallbackNum);
  }

    return null;
}

const TOKEN_ALIASES = { WVET:'VET', BVET:'VET', VVET:'VET' };
export const normToken = t => (TOKEN_ALIASES[(t||'').toUpperCase()] || (t||'').toUpperCase());

const relEps = (amt, other=amt) => Math.max(1e-8, Math.abs(amt)*1e-6, 0.5*Math.pow(10,-Math.max(2, (String(other).split('.')[1]||'').length)));

// optionale Drift-Ermittlung (Median-Shift), falls entdeckt
function computeMedianShift(custodyRows, onchainIndexByAmountTokenDir) {
  const diffs = [];
  const WINDOW = 6 * 3600; // großzügig für CEST/CET
  for (const r of custodyRows) {
    const key = `${r.token}|${r.direction}|${r.amount}`;
    const candidates = onchainIndexByAmountTokenDir.get(key) || [];
    for (const t of candidates) {
      const dt = t.timestamp - r.timestamp;
      if (Math.abs(dt) <= WINDOW) diffs.push(dt);
    }
  }
  if (!diffs.length) return 0;
  diffs.sort((a,b)=>a-b);
  const mid = Math.floor(diffs.length/2);
  return diffs.length % 2 ? diffs[mid] : Math.round((diffs[mid-1]+diffs[mid])/2);
}


// Hilfsfunktion für das Laden von Exchange-Imports (für Engine-Integration)
async function loadExchangeImports(datasetId) {
  // TODO: Implementiere das Laden der Exchange-Imports aus der Datenbank
  // Für jetzt: Placeholder
    return [];
}

function toUtc(v, opts = {}) {
  if (v == null || v === '') return null;
  return parseExchangeInstant(v, opts).iso;
}

// 🌐 Zentrale Side-Normalisierung für mehrsprachige Header
function normalizeSide(side) {
  if (!side) return null;
  
  const sideMappings = {
    'buy': ['buy', 'kauf', '買', '매수', '购买', '購入'],
    'sell': ['sell', 'verkauf', '賣', '매도', '出售', '売却']
  };
  
  const normalizedSide = String(side).toLowerCase().trim();
  
  for (const [standardSide, variants] of Object.entries(sideMappings)) {
    if (variants.includes(normalizedSide)) {
      return standardSide;
    }
  }
  
  // Fallback: return original if no mapping found
  return normalizedSide;
}

function splitPair(pair){
  if (!pair) return [undefined, undefined];
  if (pair.includes('/')) return pair.split('/');
  if (pair.includes('_')) return pair.split('_');
  if (pair.includes('-')) {
    const parts = pair.split('-');
    if (parts.length === 2) return parts;
  }
  // heuristik: SUFFIX-Quote (USDT, USD, EUR, BTC, ETH)
  const QUOTES = ['USDT','USD','EUR','BTC','ETH','KRW'];
  for (const q of QUOTES) {
    if (pair.endsWith(q)) return [pair.slice(0, pair.length - q.length), q];
  }
  return [pair, undefined];
}

function parseFee(feeField, feeCoinField){
  if (feeCoinField) return { amount: num(feeField), asset: String(feeCoinField || '').toUpperCase() };
  const s = String(feeField || '').trim(); // z.B. "0.0001 BNB" oder "0.10"
  if (!s) return { amount: undefined, asset: undefined };
  const m = s.match(/^\s*([\-+]?\d+(\.\d+)?)\s*([A-Za-z0-9]+)?\s*$/);
  if (m) return { amount: Number(m[1]), asset: (m[3] || '').toUpperCase() || undefined };
  return { amount: num(s), asset: undefined };
}

function normalizeKrakenPair(p){
  let [b, q] = String(p||'').toUpperCase().split('/');
  if (b==='XBT') b='BTC';
  return [b, q];
}

function num(v){
  if (v == null || v === '') return undefined;
  let s = String(v).trim();

  // (1.23) -> -1.23
  let sign = 1;
  if (/^\(.*\)$/.test(s)) {
    sign = -1;
    s = s.slice(1, -1).trim();
  }

  // Remove currency symbols / percent signs
  s = s.replace(/[€$£¥₿₽₹₩₪₫₴₦₱₲₵₺₸%]/g, '');

  // Fast path: scientific notation (e.g. 1e-8) – parse before stripping letters
  const compact = s.replace(/\s+/g, '');
  if (/^[+-]?(?:\d+(?:\.\d+)?|\.\d+)[eE][+-]?\d+$/.test(compact)) {
    const nSci = Number(compact);
    return Number.isFinite(nSci) ? nSci * sign : undefined;
  }

  // Remove all letters (currency codes, token suffixes like "BNB")
  s = s.replace(/[A-Za-z]/g, '');
  // remove spaces as thousand separators
  s = s.replace(/\s+/g, '');

  // 1. EU decimals? e.g. 1.234,56 or 0,1234
  const looksEU = /,\d{1,8}$/.test(s) || (detectedSep === ';' && s.includes(','));
  if (looksEU) {
    // remove thousand separators . or ' then replace decimal comma with dot
    s = s.replace(/\.(?=\d{3}\b)/g,''); // 1.234 -> 1234
    s = s.replace(/'(?=\d{3}\b)/g,'');  // 1'234 -> 1234
    s = s.replace(',', '.');            // 123,45 -> 123.45
  } else {
    // US style: remove thousand commas
    s = s.replace(/,(?=\d{3}\b)/g,'');
  }

  const n = Number(s);
  const out = Number.isFinite(n) ? n * sign : undefined;
  return out;
}
function isNum(v){ return typeof v === 'number' && !isNaN(v); }
function round(n){ return isNum(n) ? Math.round(n*1e12)/1e12 : undefined; }

// Pairing, Transfers, Plausibilität, Dedupe
function postProcess(arr){
  const issues = [];
  let out = Array.isArray(arr) ? [...arr] : [];

  // 1) Kraken: Trades-CSV verdrängt Ledger-Legs nur innerhalb desselben Accounts
  const krakenRichScopes = new Set();
  for (const tx of out) {
    if (
      String(tx.exchange || '').toLowerCase() === 'kraken' &&
      String(tx.type || '').toLowerCase() === 'trade' &&
      (isNum(tx.price) ||
        (tx.quote && String(tx.quote).length > 0) ||
        (tx.quoteAsset && String(tx.quoteAsset).length > 0))
    ) {
      krakenRichScopes.add(buildExchangeAccountScopeKey(tx));
    }
  }

  if (krakenRichScopes.size > 0) {
    out = out.filter((tx) => {
      const isLeg =
        String(tx.exchange || '').toLowerCase() === 'kraken' &&
        String(tx.type || '').toLowerCase() === 'ledger_trade_leg';
      if (!isLeg) return true;
      return !krakenRichScopes.has(buildExchangeAccountScopeKey(tx));
    });
  }

  out = pairKrakenLedgerTrades(out, issues);

  // 2) OKX / Huobi / Crypto.com trade_raw pairing (account-scoped)
  out = pairRawLedgerTrades(out, issues);

  // 3) Interne Account-Transfers paaren
  out = pairAccountTransfers(out, issues);

  // 4) MEXC / Binance Fallbacks pro Account-Scope
  const mexcTradeScopes = new Set();
  const binanceTradeScopes = new Set();
  for (const tx of out) {
    const ex = String(tx.exchange || '').toLowerCase();
    const src = String(tx.importSource || '');
    if (ex === 'mexc' && src === 'trade_history') {
      mexcTradeScopes.add(buildExchangeAccountScopeKey(tx));
    }
    if (ex === 'binance' && src === 'trade_history') {
      binanceTradeScopes.add(buildExchangeAccountScopeKey(tx));
    }
  }

  if (mexcTradeScopes.size > 0) {
    out = out.filter((tx) => {
      const drop =
        String(tx.exchange || '').toLowerCase() === 'mexc' &&
        String(tx.importSource || '') === 'order_history' &&
        mexcTradeScopes.has(buildExchangeAccountScopeKey(tx));
      return !drop;
    });
  }

  if (binanceTradeScopes.size > 0) {
    out = out.filter((tx) => {
      const drop =
        String(tx.exchange || '').toLowerCase() === 'binance' &&
        String(tx.importSource || '') === 'order_history' &&
        binanceTradeScopes.has(buildExchangeAccountScopeKey(tx));
      return !drop;
    });
  }

  // 5) Staging-Typen dürfen nicht übrig bleiben
  const stagingTypes = new Set(['trade_raw', 'ledger_trade_leg', 'transfer']);
  const remaining = [];
  const canonical = [];
  for (const tx of out) {
    const t = String(tx.type || '').toLowerCase();
    if (stagingTypes.has(t)) {
      remaining.push(tx);
      issues.push(
        makeImportIssue({
          file: tx.sourceFile || tx.file,
          row: tx.row,
          exchange: tx.exchange,
          account: pickExchangeAccountId(tx),
          code:
            t === 'transfer'
              ? 'UNRESOLVED_ACCOUNT_TRANSFER'
              : 'UNRESOLVED_TRADE_PAIR',
          severity: 'error',
          message:
            `Unresolved staging row type="${t}" for ${tx.exchange || 'unknown'}` +
            ` account=${pickExchangeAccountId(tx)}` +
            (tx.txId || tx.orderId || tx.transferId || tx.refId
              ? ` ref=${tx.txId || tx.orderId || tx.transferId || tx.refId}`
              : ''),
        })
      );
    } else {
      canonical.push(ensureImportUid(tx));
    }
  }

  const deduped = dedupe(canonical, (tx) =>
    buildExchangeRowFingerprint({
      source: tx.exchange,
      exchange: tx.exchange,
      exchangeAccountId: pickExchangeAccountId(tx),
      kind: tx.type,
      type: tx.type,
      txid: tx.txId || tx.txid,
      timestamp: tx.timestamp,
      side: tx.side,
      base: tx.baseAsset || tx.base,
      quote: tx.quoteAsset || tx.quote,
      amount: tx.baseAmount ?? tx.amount,
      quoteAmount: tx.quoteAmount,
      price: tx.price,
      fee: tx.feeAmount ?? tx.fee,
      feeAsset: tx.feeAsset,
      importSource: tx.importSource,
      importUid: tx.importUid || tx.import_uid,
    })
  );

  return { transactions: deduped, issues };
}

/**
 * Pair OKX / Huobi / Crypto.com trade_raw rows into canonical trades.
 * Groups by <exchange>|<accountId>|<nativeReference>.
 */
function pairRawLedgerTrades(rows, issues = []) {
  const out = [];
  const groups = new Map();
  const feesByRef = new Map();

  const nativeRefOf = (r) =>
    String(
      r.orderId ||
        r.transferId ||
        r.remark ||
        r.txId ||
        r.txid ||
        ''
    ).trim();

  for (const r of rows) {
    const ex = String(r.exchange || '').toLowerCase();
    const t = String(r.type || '').toLowerCase();

    if (t === 'fee' && (ex === 'okx' || ex === 'bitget')) {
      const ref = nativeRefOf(r);
      if (ref) {
        const key = `${ex}|${pickExchangeAccountId(r)}|${ref}`;
        if (!feesByRef.has(key)) feesByRef.set(key, []);
        feesByRef.get(key).push(r);
        continue;
      }
    }

    if (
      t === 'trade_raw' &&
      (ex === 'okx' || ex === 'huobi' || ex === 'crypto_com')
    ) {
      const ref = nativeRefOf(r);
      if (!ref) {
        issues.push(
          makeImportIssue({
            file: r.sourceFile,
            row: r.row,
            exchange: r.exchange,
            account: pickExchangeAccountId(r),
            code: 'UNRESOLVED_TRADE_PAIR',
            severity: 'error',
            message: `trade_raw missing native reference on ${ex}`,
          })
        );
        continue;
      }
      const key = `${ex}|${pickExchangeAccountId(r)}|${ref}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
      continue;
    }

    out.push(r);
  }

  for (const [key, legs] of groups.entries()) {
    const [ex] = key.split('|');
    const signedLegs = legs.map((leg) => {
      let signed = Number(leg.amount ?? leg.signedAmount ?? 0);
      if (ex === 'huobi') {
        const inOut = String(leg.inOut || '').toLowerCase();
        if (inOut === 'in') signed = Math.abs(signed);
        else if (inOut === 'out') signed = -Math.abs(signed);
      }
      const asset = normalizeSymbol(
        leg.asset || leg.currency || leg.base || leg.baseAsset
      );
      return { ...leg, signedAmount: signed, asset };
    });

    // Aggregate same-asset legs
    const byAsset = new Map();
    for (const leg of signedLegs) {
      if (!leg.asset || !Number.isFinite(leg.signedAmount)) continue;
      byAsset.set(
        leg.asset,
        (byAsset.get(leg.asset) || 0) + leg.signedAmount
      );
    }

    const spends = [];
    const receives = [];
    for (const [asset, amt] of byAsset.entries()) {
      if (Math.abs(amt) <= 1e-12) continue;
      if (amt < 0) spends.push({ asset, amount: Math.abs(amt) });
      else receives.push({ asset, amount: Math.abs(amt) });
    }

    if (spends.length !== 1 || receives.length !== 1) {
      issues.push(
        makeImportIssue({
          file: legs[0]?.sourceFile,
          row: legs[0]?.row,
          exchange: legs[0]?.exchange,
          account: pickExchangeAccountId(legs[0] || {}),
          code:
            spends.length === 0 || receives.length === 0
              ? 'UNRESOLVED_TRADE_PAIR'
              : 'AMBIGUOUS_TRADE_PAIR',
          severity: 'error',
          message:
            `Cannot pair trade_raw group ${key}: spends=${spends.length} receives=${receives.length}`,
        })
      );
      continue;
    }

    const spend = spends[0];
    const receive = receives[0];
    const spendIsFiat = FIAT.has(spend.asset) || QUOTE_ASSETS.has(spend.asset);
    const receiveIsFiat =
      FIAT.has(receive.asset) || QUOTE_ASSETS.has(receive.asset);
    const spendIsWhite = isWhitelisted(spend.asset);
    const receiveIsWhite = isWhitelisted(receive.asset);

    let side = 'BUY';
    let base = receive.asset;
    let quote = spend.asset;
    let baseAmount = receive.amount;
    let quoteAmount = spend.amount;

    if (receiveIsWhite && spendIsFiat) {
      side = 'BUY';
      base = receive.asset;
      baseAmount = receive.amount;
      quote = spend.asset;
      quoteAmount = spend.amount;
    } else if (spendIsWhite && receiveIsFiat) {
      side = 'SELL';
      base = spend.asset;
      baseAmount = spend.amount;
      quote = receive.asset;
      quoteAmount = receive.amount;
    } else if (receiveIsWhite || spendIsWhite) {
      // crypto-crypto: keep receive as base (BUY) — Engine/recompute expands both sides later
      side = 'BUY';
      base = receive.asset;
      baseAmount = receive.amount;
      quote = spend.asset;
      quoteAmount = spend.amount;
    } else {
      issues.push(
        makeImportIssue({
          file: legs[0]?.sourceFile,
          row: legs[0]?.row,
          exchange: legs[0]?.exchange,
          account: pickExchangeAccountId(legs[0] || {}),
          code: 'UNRESOLVED_TRADE_PAIR',
          severity: 'error',
          message: `trade_raw group ${key} has no VeChain-relevant asset`,
        })
      );
      continue;
    }

    const feeBag = feesByRef.get(key) || [];
    feesByRef.delete(key);
    const bestFee = feeBag
      .filter((f) => isNum(f.feeAmount || f.baseAmount) && (f.feeAmount || f.baseAmount) > 0)
      .sort(
        (a, b) =>
          Math.abs(b.feeAmount || b.baseAmount || 0) -
          Math.abs(a.feeAmount || a.baseAmount || 0)
      )[0];

    const price =
      isNum(baseAmount) && baseAmount > 0 && isNum(quoteAmount)
        ? quoteAmount / baseAmount
        : undefined;
    const ts = legs.reduce(
      (m, x) => (m == null ? x.timestamp : Math.min(m, x.timestamp)),
      null
    );
    const accountId = pickExchangeAccountId(legs[0] || {});
    const ref = nativeRefOf(legs[0] || {});

    out.push(
      ensureImportUid({
        exchange: ex,
        exchangeAccountId: accountId,
        exchange_account_id: accountId,
        sourceFile: legs[0]?.sourceFile,
        row: legs[0]?.row,
        timestamp: ts,
        type: 'trade',
        side,
        baseAsset: base,
        baseAmount,
        quoteAsset: quote,
        quoteAmount,
        price,
        feeAsset: bestFee
          ? normalizeSymbol(
              bestFee.feeAsset || bestFee.baseAsset || bestFee.asset || bestFee.base
            )
          : undefined,
        feeAmount: bestFee
          ? Math.abs(Number(bestFee.feeAmount || bestFee.baseAmount || 0))
          : undefined,
        txId: ref,
        note: `${ex} paired ledger trade (ref=${ref})`,
        importSource: 'ledger_pair',
      })
    );
  }

  // leftover fees not attached to a trade — keep as standalone fee rows
  for (const feeLegs of feesByRef.values()) {
    out.push(...feeLegs);
  }

  return out;
}

/**
 * Pair temporary transfer rows into account_transfer when opposite legs share a reference.
 * Unresolved transfers remain as type=transfer for the staging check to block.
 */
function pairAccountTransfers(rows, issues = []) {
  const out = [];
  const groups = new Map();

  for (const r of rows) {
    const t = String(r.type || '').toLowerCase();
    if (t !== 'transfer') {
      out.push(r);
      continue;
    }
    const ex = String(r.exchange || '').toLowerCase();
    const ref = String(r.txId || r.txid || r.orderId || r.transferId || '').trim();
    const asset = normalizeSymbol(r.baseAsset || r.base || r.asset);
    if (!ref || !asset) {
      out.push(r);
      continue;
    }
    const key = `${ex}|${asset}|${ref}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }

  for (const [key, legs] of groups.entries()) {
    if (legs.length < 2) {
      out.push(...legs);
      continue;
    }

    const withSigned = legs.map((leg) => {
      const amt = Number(leg.baseAmount ?? leg.amount ?? 0);
      // Prefer explicit signed amount fields if present
      const signed =
        Number.isFinite(Number(leg.signedAmount))
          ? Number(leg.signedAmount)
          : Number.isFinite(Number(leg.amount)) && Number(leg.amount) < 0
            ? Number(leg.amount)
            : amt;
      return { ...leg, _signed: signed };
    });

    const outs = withSigned.filter((x) => x._signed < 0);
    const ins = withSigned.filter((x) => x._signed > 0);

    if (outs.length !== 1 || ins.length !== 1) {
      // Keep as staging → blocking later unless clearly unpairable accounts already set
      out.push(...legs);
      continue;
    }

    const fromLeg = outs[0];
    const toLeg = ins[0];
    const sourceAccount = pickExchangeAccountId(fromLeg);
    const targetAccount = pickExchangeAccountId(toLeg);
    const amount = Math.abs(fromLeg._signed);
    const asset = normalizeSymbol(
      fromLeg.baseAsset || fromLeg.base || fromLeg.asset
    );
    const ts = Math.min(
      Number(fromLeg.timestamp || 0),
      Number(toLeg.timestamp || 0)
    );
    const ref = String(
      fromLeg.txId || fromLeg.txid || fromLeg.orderId || fromLeg.transferId || ''
    );

    out.push(
      ensureImportUid({
        exchange: String(fromLeg.exchange || '').toLowerCase(),
        exchangeAccountId: sourceAccount,
        exchange_account_id: sourceAccount,
        sourceAccountId: sourceAccount,
        source_account_id: sourceAccount,
        targetAccountId: targetAccount,
        target_account_id: targetAccount,
        sourceFile: fromLeg.sourceFile,
        row: fromLeg.row,
        timestamp: ts,
        type: 'account_transfer',
        baseAsset: asset,
        baseAmount: amount,
        txId: ref,
        note: `account transfer ${sourceAccount}→${targetAccount} (${key})`,
        importSource: 'account_transfer_pair',
      })
    );
  }

  return out;
}

function pairKrakenLedgerTrades(rows, issues = []) {
  const out = [];
  const groups = new Map();

  for (const r of rows) {
    const isLeg =
      (r.exchange || '').toLowerCase() === 'kraken' &&
      (r.type || '').toLowerCase() === 'ledger_trade_leg' &&
      r.refId;

    if (!isLeg) {
      out.push(r);
      continue;
    }

    const accountId = pickExchangeAccountId(r);
    const key = `kraken|${accountId}|${String(r.refId)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }

  for (const [groupKey, legs] of groups.entries()) {
    // typischerweise 2 Legs: ein negativer (spend) & ein positiver (receive)
    const neg = legs
      .filter(x => isNum(x.signedAmount) && x.signedAmount < 0)
      .sort((a,b) => Math.abs(b.signedAmount) - Math.abs(a.signedAmount))[0];
    const pos = legs
      .filter(x => isNum(x.signedAmount) && x.signedAmount > 0)
      .sort((a,b) => Math.abs(b.signedAmount) - Math.abs(a.signedAmount))[0];

    if (!neg || !pos) {
      issues.push(
        makeImportIssue({
          file: legs[0]?.sourceFile,
          row: legs[0]?.row,
          exchange: 'kraken',
          account: pickExchangeAccountId(legs[0] || {}),
          code: 'UNRESOLVED_TRADE_PAIR',
          severity: 'error',
          message: `Kraken ledger legs not pairable for ${groupKey}`,
        })
      );
      continue;
    }

    const spendAsset = normalizeSymbol(neg.baseAsset);
    const receiveAsset = normalizeSymbol(pos.baseAsset);
    const spendAmt = Math.abs(neg.signedAmount);
    const receiveAmt = Math.abs(pos.signedAmount);

    // Fee: nimm die größte Fee aus den Legs (Kraken Ledger Fee ist im jeweiligen Asset)
    const bestFeeLeg = legs
      .filter(x => isNum(x.feeAmount) && x.feeAmount > 0)
      .sort((a,b) => (b.feeAmount||0) - (a.feeAmount||0))[0];
    const feeAmount = bestFeeLeg?.feeAmount;
    const feeAsset = bestFeeLeg?.feeAsset;

    // Side + Base/Quote Heuristik:
    // - Wenn receive whitelisted und spend Fiat -> BUY receive/base, quote=fiat
    // - Wenn spend whitelisted und receive Fiat -> SELL spend/base, quote=fiat
    // - Sonst: base=receive, quote=spend (Swap), side='BUY' (neutral)
    const spendIsFiat = FIAT.has(spendAsset);
    const receiveIsFiat = FIAT.has(receiveAsset);
    const spendIsWhite = isWhitelisted(spendAsset);
    const receiveIsWhite = isWhitelisted(receiveAsset);

    let side = 'BUY';
    let base = receiveAsset;
    let quote = spendAsset;
    let baseAmount = receiveAmt;
    let quoteAmount = spendAmt;

    if (receiveIsWhite && spendIsFiat) {
      side = 'BUY';
      base = receiveAsset;  baseAmount = receiveAmt;
      quote = spendAsset;   quoteAmount = spendAmt;
    } else if (spendIsWhite && receiveIsFiat) {
      side = 'SELL';
      base = spendAsset;    baseAmount = spendAmt;
      quote = receiveAsset; quoteAmount = receiveAmt;
    } else {
      // Swap / crypto-crypto: treat as BUY of receive using spend as quote
      side = 'BUY';
      base = receiveAsset;  baseAmount = receiveAmt;
      quote = spendAsset;   quoteAmount = spendAmt;
    }

    const price = (isNum(baseAmount) && baseAmount > 0 && isNum(quoteAmount))
      ? (quoteAmount / baseAmount)
      : undefined;

    // Timestamp: nimm früheste Leg-Zeit
    const ts = legs.reduce((m, x) => (m == null ? x.timestamp : Math.min(m, x.timestamp)), null);

    const ledgerAccountId = pickExchangeAccountId(legs[0] || {});
    const refId = legs[0]?.refId;

    out.push(ensureImportUid({
      exchange: 'kraken',
      exchangeAccountId: ledgerAccountId,
      exchange_account_id: ledgerAccountId,
      sourceFile: legs[0]?.sourceFile,
      row: legs[0]?.row,
      timestamp: ts,
      type: 'trade',
      side,
      baseAsset: base,
      baseAmount,
      quoteAsset: quote,
      quoteAmount,
      price,
      feeAsset,
      feeAmount,
      txId: legs.find(x => x.txId)?.txId,
      note: `Kraken ledger trade (refid=${refId})`,
      importSource: 'ledger'
    }));
  }

  return out;
}

function dedupe(list, keyFn){
  const seen = new Set(); const out=[];
  for (const x of list){
    const k = keyFn(x);
    if (seen.has(k)) continue;
    seen.add(k); out.push(x);
  }
  return out;
}

export function convertExchangeImportsToTaxData(exchangeRows, year) {
  const acquisitions = [];
  const disposals = [];
  const custody = [];
  const incomes = [];
  
  // Bitpanda: Amount netto, Fee separat. Bitvavo: Amount brutto inkl. Fee.
  const WITHDRAW_GROSS = new Set(['bitpanda', 'bitvavo']);
  
  for (const r0 of exchangeRows) {
    // normalize row
    const r = {
      ...r0,
      exchange: (r0.exchange||r0.source||'').toLowerCase(),
      type: (r0.type||r0.kind||r0.action||'').toLowerCase(),
      side: (r0.side||'').toUpperCase(),
      token: normToken(r0.token||r0.asset||r0.baseAsset||r0.base),
      baseAmount: Number(r0.baseAmount ?? r0.amount ?? r0.quantity ?? 0),
      feeToken: r0.feeToken ? normToken(r0.feeToken) : (r0.fee_asset ? normToken(r0.fee_asset) : undefined),
      feeAmount: r0.feeAmount ? Number(r0.feeAmount) : (r0.fee ? Number(r0.fee) : 0),
      timestamp: toEpochUtc(r0.timestamp),
      txid: r0.txid || null,
      id: r0.id || r0.order_id || r0.transfer_id || `${r0.exchange||r0.source}-${r0.timestamp}-${r0.token||r0.base}-${r0.amount}`
    };
    const t = r.type;
    const base = {
      id: r.id, exchange: r.exchange || 'unknown',
      token: r.token, amount: r.baseAmount,
      feeToken: r.feeToken, feeAmount: r.feeAmount,
      timestamp: r.timestamp, txid: r.txid || null,
      source: 'exchange',
    };
    if (t === 'trade' && r.side === 'BUY') {
      acquisitions.push({ ...base, classification: 'exchange_trade_buy' });
      // 🚀 WICHTIG: Trade-Fees nur als Outflow wenn in Krypto bezahlt (FIAT-Fees sind keine FIFO-Outflows)
      if (r.feeAmount > 0 && r.feeToken && !FIAT.has(r.feeToken)) {
        disposals.push({
          ...base,
          token: normToken(r.feeToken),
          amount: r.feeAmount,
          classification: 'exchange_trade_fee'
        });
              }
      continue;
    }
    if (t === 'trade' && r.side === 'SELL') {
      disposals.push({ ...base, classification: 'exchange_trade_sell' });
      // 🚀 WICHTIG: Trade-Fees nur als Outflow wenn in Krypto bezahlt (FIAT-Fees sind keine FIFO-Outflows)
      if (r.feeAmount > 0 && r.feeToken && !FIAT.has(r.feeToken)) {
        disposals.push({
          ...base,
          token: normToken(r.feeToken),
          amount: r.feeAmount,
          classification: 'exchange_trade_fee'
        });
              }
      continue;
    }
    if (t === 'income' || t === 'staking' || t === 'airdrop') {
      const incomeRow = { ...base, classification: 'exchange_income' };
      acquisitions.push(incomeRow); // erzeugt FIFO-Lot
      incomes.push(incomeRow);      // explizit für spätere Auswertung
      continue;
    }
    if (t === 'deposit' || t === 'withdrawal') {
      const direction = t === 'deposit' ? 'sent' : 'received';
      
      // 🚀 NEU: Custody-Amount für Bitpanda-Withdrawals Brutto->Netto, sonst unverändert
      let custodyAmount = r.baseAmount;
      if (t === 'withdrawal' && WITHDRAW_GROSS.has(r.exchange)) {
        // Wenn Fee in gleichem Asset (oder Fee-Asset fehlt), ziehe sie vom Amount ab
        const sameAssetOrUnknown = !r.feeToken || normToken(r.feeToken) === r.token;
        if (sameAssetOrUnknown && r.feeAmount > 0) {
          custodyAmount = Math.max(0, Number(r.baseAmount) - r.feeAmount);
                  }
      }
      
      // ✅ Custody-Zeile (für On-chain-Matching)
      custody.push({ 
        ...base, 
        amount: custodyAmount,
        direction, 
        classification: t === 'deposit' ? 'exchange_deposit' : 'exchange_withdrawal' 
      });
      
      // ✅ Withdrawal-Fee nur als Disposal verbuchen wenn in Krypto bezahlt (FIAT-Fees sind keine FIFO-Outflows)
      if (t === 'withdrawal' && r.feeAmount > 0) {
        const feeToken = r.feeToken || r.token; // Falls kein feeToken, verwende Basis-Token
        // Nur als Disposal behandeln wenn Fee in Krypto bezahlt wurde (nicht FIAT)
        if (feeToken && !FIAT.has(feeToken)) {
          // 🚀 FIX: Fee-Event eindeutig & FIFO-relevant erzeugen
          const feeEvent = {
            ...base,
            token: normToken(feeToken),
            amount: r.feeAmount,
            classification: 'exchange_withdrawal_fee',
            hash: `${r.txid || r.id}:fee`, // eindeutiger Hash mit :fee-Suffix
            includeInFifo: true,
            excludeFromCustodyNeutralization: true, // wichtig: nicht als "internal" wegneutralisieren
            excludeFromFifo: false,
            excludeFromMatching: true,
            taxable: false,
            isFee: true
          };
          disposals.push(feeEvent);
                  } else {
                  }
      }
      
      // ✅ Deposit-Fee nur als Disposal verbuchen wenn in Krypto bezahlt (FIAT-Fees sind keine FIFO-Outflows)
      if (t === 'deposit' && r.feeAmount > 0) {
        const feeToken = r.feeToken || r.token; // Falls kein feeToken, verwende Basis-Token
        // Nur als Disposal behandeln wenn Fee in Krypto bezahlt wurde (nicht FIAT)
        if (feeToken && !FIAT.has(feeToken)) {
          // 🚀 FIX: Fee-Event eindeutig & FIFO-relevant erzeugen
          const feeEvent = {
            ...base,
            token: normToken(feeToken),
            amount: r.feeAmount,
            classification: 'exchange_deposit_fee',
            hash: `${r.txid || r.id}:fee`, // eindeutiger Hash mit :fee-Suffix
            includeInFifo: true,
            excludeFromCustodyNeutralization: true, // wichtig: nicht als "internal" wegneutralisieren
            excludeFromFifo: false,
            excludeFromMatching: true,
            taxable: false,
            isFee: true
          };
          disposals.push(feeEvent);
                  } else {
                  }
      }
      
      continue;
    }
  }
  return { acquisitions, disposals, custody, incomes };
}

export function mergeExchangeTradesIntoTransfersClassified(tClass, acquisitions, disposals, custody) {
  // BUY → INFLOW
  for (const a of acquisitions) {
    const token = a.token.toUpperCase();
    tClass.inflowsByToken[token] ||= [];
    tClass.inflowsByToken[token].push({
      token, amount: a.amount, timestamp: a.timestamp,
      hash: a.txid || a.id, classification: a.classification || 'exchange_trade_buy',
      source: 'exchange', exchange: a.exchange,
      unitFiat: a.unitFiat ?? null, totalFiat: a.totalFiat ?? null, fiatCurrency: a.fiatCurrency ?? 'EUR',
    });
  }
  // SELL/FEE → OUTFLOW
  for (const d of disposals) {
    const token = d.token.toUpperCase();
    tClass.outflowsByToken[token] ||= [];
    tClass.outflowsByToken[token].push({
      token, amount: d.amount, timestamp: d.timestamp,
      hash: d.txid || d.id, classification: d.classification, // sell|fee
      source: 'exchange', exchange: d.exchange,
    });
  }
  // Custody → INTERNALS (neutral)
  for (const c of custody) {
    const token = c.token.toUpperCase();
    tClass.internalsByToken[token] ||= [];
    tClass.internalsByToken[token].push({
      token, amount: c.amount, timestamp: c.timestamp,
      hash: c.txid || c.id, classification: c.classification, // deposit|withdrawal
      source: 'exchange', exchange: c.exchange,
    });
  }
}

// 🚀 NEU: Robuster Index über alle On-chain Transaktionen
function buildOnchainIndex(vip180, vetvtho) {
  const all = [...(vip180 || []), ...(vetvtho || [])];
  const byHash = new Map();
  const byKey = new Map(); // Fallback: token|amount|day|dir

  
  for (const tx of all) {
    // Hash-Index (bevorzugt für exakte Matches)
    if (tx.hash || tx.txid) {
      const hash = (tx.hash || tx.txid).toLowerCase();
      byHash.set(hash, tx);
    }

    // Richtung aus Sicht des Users (sent/received)
    // Prüfe classification, type, direction
    const rawDir = tx.classification || tx.type || tx.direction || '';
    let dir = '';
    if (rawDir.includes('sent') || rawDir === 'sent') {
      dir = 'sent';
    } else if (rawDir.includes('received') || rawDir === 'received') {
      dir = 'received';
    } else if (rawDir.includes('withdrawal')) {
      dir = 'sent'; // Withdrawal = User sendet aus Wallet raus
    } else if (rawDir.includes('deposit')) {
      dir = 'received'; // Deposit = User empfängt in Wallet
    }

    if (!dir) continue; // Nur sent/received sind relevant

    // Token-Symbol normalisieren
    const token = (tx.token || tx.token_symbol || tx.symbol || tx.asset || tx.baseAsset || '').toUpperCase();
    if (!token) continue;

    // Amount extrahieren
    const amount = Number(tx.amount ?? tx.value ?? tx.baseAmount ?? 0);
    if (!amount || amount <= 0) continue;

    // Timestamp zu Tag konvertieren
    const timestamp = tx.timestamp || tx.block_timestamp || 0;
    const day = new Date(timestamp * 1000).toISOString().slice(0, 10);

    // Key: token|amount|day|dir
    const key = `${token}|${amount}|${day}|${dir}`;
    if (!byKey.has(key)) {
      byKey.set(key, []);
    }
    byKey.get(key).push(tx);
  }

    return { byHash, byKey, all };
}

// Custody an Chain-Transfers matchen und labeln
function linkExchangeCustodyToChain(custodyRows, vetVthoRows, vip180Rows, knownExchangeAddrs=new Set()) {
  const WINDOW = 3*3600; // 3h
  
    
  // 🚀 NEU: Robuster Index bauen
  const { byHash, byKey, all } = buildOnchainIndex(vip180Rows, vetVthoRows);

  if (all.length === 0) {
        return;
  }

  let matchedCount = 0;

  for (const c of custodyRows) {
    const token = (c.token||'').toUpperCase();
    const amount = Number(c.amount || 0);
    const timestamp = c.timestamp || 0;
    
    // 🚀 WICHTIG: direction kommt aus custody-Objekt
    // custody.push({ ...base, direction, classification: t === 'deposit' ? 'exchange_deposit' : 'exchange_withdrawal' });
    // direction: t === 'deposit' ? 'sent' : 'received'
    const dir = c.direction; // 'sent' oder 'received'
    
    if (!dir) {
            continue;
    }
    
    const day = new Date(timestamp * 1000).toISOString().slice(0, 10);

    // 1) Versuche Hash-Match (falls Exchange TxID verfügbar)
    let best = null;
    if (c.txid) {
      const hashMatch = byHash.get(c.txid.toLowerCase());
      if (hashMatch) {
        best = hashMatch;
              }
    }

    // 2) Fallback: Key-Match (token|amount|day|dir)
    if (!best) {
      const key = `${token}|${amount}|${day}|${dir}`;
      const candidates = byKey.get(key) || [];
      
      let bestScore = -1;
      for (const t of candidates) {
        const dt = Math.abs((t.timestamp || t.block_timestamp || 0) - timestamp);
        if (dt > WINDOW) continue;
        
        const amt = Number(t.amount ?? t.value ?? t.baseAmount ?? 0);
        const diff = Math.abs(amt - amount);
        if (diff > relEps(amount, amt)) continue;
        
        let score = -dt;
        if (t.from && knownExchangeAddrs.has(String(t.from).toLowerCase())) score += 1000;
        if (t.to && knownExchangeAddrs.has(String(t.to).toLowerCase())) score += 1000;
        
        if (score > bestScore) {
          bestScore = score;
          best = t;
        }
      }
    }

    // 3) Match gefunden → Markieren
    if (best) {
      // 🚀 WICHTIG: Überschreibe classification - verwende die classification aus custody
      const newClassification = c.classification; // 'exchange_withdrawal' oder 'exchange_deposit'
      best.classification = newClassification;
      best.linkedExchangeId = c.id;
      best.exchange = c.exchange;
      best.exchangeTxid = c.txid || null;
      best.match_confidence = Math.min(1, Math.max(0, 0.9)); // High confidence
      best.seconds_offset = (best.timestamp || best.block_timestamp || 0) - timestamp;
      best.flags = Array.isArray(best.flags) ? best.flags : [];
      if (!best.flags.includes('linked_exchange')) best.flags.push('linked_exchange');
      
      matchedCount++;
          }
  }

  }
